import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import HTMLContent from "../components/common/HtmlContent";
import {
  auth,
  cars,
  coupons,
  rentals,
  contact,
  drivers,
  transferPricing,
  partners,
  tours,
  tourBookings,
} from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner";
import NotificationSystem from "../components/Notification/NotificationSystem";
import {
  FaCar,
  FaTicketAlt,
  FaUsers,
  FaHandshake,
  FaTrash,
  FaEdit,
  FaFilter,
  FaChevronDown,
  FaChevronUp,
  FaBaby,
  FaChild,
  FaMoneyBill,
  FaPercent,
  FaTag,
  FaClock,
  FaCheck,
  FaBan,
  FaCheckCircle,
  FaTools,
  FaSearch,
  FaPlus,
  FaUser,
  FaCog,
  FaGlobe,
  FaSuitcase,
  FaGasPump,
  FaCalendarDay,
  FaCalendarAlt,
  FaCalendarWeek,
  FaPercentage,
  FaShoppingCart,
  FaEuroSign,
  FaDownload,
  FaExchangeAlt,
  FaTaxi,
  FaIdCard,
  FaUserTie,
  FaMapMarkerAlt,
  FaTruckMoving,
  FaPhoneAlt,
  FaEnvelope,
  FaTags,
} from "react-icons/fa";
import { TbGps } from "react-icons/tb";

import moment from "moment-timezone";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import withReactContent from 'sweetalert2-react-content';
import CreateTourForm from "../components/TourForm/CreateTourForm";
import EditTourForm from "../components/TourForm/EditTourForm";
import CarDetailsModal from "../components/CarDetailsModal/CarDetailsModal";
import FinanceManagement from "../components/FinanceManagement/FinanceManagement";
import PartnerManagement from "../components/PartnerManagement/PartnerManagement";
import CreateCarModal from "../components/CarDetailsModal/CreateCarModal";
import { generateDailyPlanPDF } from "../utils/pdfGenerator";

// Import all needed images
import { categories } from "../data/carData";
import TransferePage from "../components/TransferPricing/TransferPricingManagement";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const MySwal = withReactContent(Swal);
  const formRef = useRef();
  const editTourRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [updatingCarId, setUpdatingCarId] = useState(null);
  const [updatingRentalId, setUpdatingRentalId] = useState(null);
  const [creatingCoupon, setCreatingCoupon] = useState(false);
  const [deletingCouponId, setDeletingCouponId] = useState(null);
  const [settingSeasonalPrice, setSettingSeasonalPrice] = useState(null);
  const [activeTab, setActiveTab] = useState(
    window.location.hostname === "localhost" ||
      window.location.hostname === "transfer-athens.online"
      ? "transfers"
      : "cars"
  );
  const [carsData, setCarsData] = useState([]);
  const [rentalsData, setRentalsData] = useState([]);
  const [couponsData, setCouponsData] = useState([]);
  const [transfersData, setTransfersData] = useState([]);
  const [driversData, setDriversData] = useState([]);
  const [partnersData, setPartners] = useState([]);
  const [toursData, setToursData] = useState([]);
  const [tourBookingsData, setTourBookingsData] = useState([]);
  const [error, setError] = useState(null);
  const [editingCar, setEditingCar] = useState(null);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedTransmission, setSelectedTransmission] = useState("all");
  const [seasonalPricing, setSeasonalPricing] = useState({});
  const [editingSeasonalPrice, setEditingSeasonalPrice] = useState(null);
  const [deletingCarId, setDeletingCarId] = useState(null);
  const [selectedCar, setSelectedCar] = useState(null);
  const [carDetailsModalOpen, setCarDetailsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreatingCar, setIsCreatingCar] = useState(false);
  const [durationDiscounts, setDurationDiscounts] = useState({
    shortTerm: { discount: 0 },
    mediumTerm: { discount: 5 },
    longTerm: { discount: 10 },
  });
  const [deletingRentalId, setDeletingRentalId] = useState(null);
  const [createCarModalOpen, setCreateCarModalOpen] = useState(false);
  const [updatingPaymentId, setUpdatingPaymentId] = useState(null);
  const [rentalSearch, setRentalSearch] = useState("");
  const [rentalStatusFilter, setRentalStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState({
    startDate: null,
    endDate: null,
  });
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [turnoverPage, setTurnoverPage] = useState({
    pickups: 1,
    returns: 1,
  });
  // Transfer management states
  const [transferSearch, setTransferSearch] = useState("");
  const [transferStatusFilter, setTransferStatusFilter] = useState("all");
  const [expandedTransfer, setExpandedTransfer] = useState(null);
  const [deletingTransferId, setDeletingTransferId] = useState(null);
  const [updatingTransferStatus, setUpdatingTransferStatus] = useState(null);
  const [assigningDriverToTransfer, setAssigningDriverToTransfer] =
    useState(null);
  const [transferDateFilter, setTransferDateFilter] = useState({
    startDate: null,
    endDate: null,
  });
  const [transferTimeFilter, setTransferTimeFilter] = useState("all");
  const [transferPartnerFilter, setTransferPartnerFilter] = useState("all");
  const [creatingPartnerTransfer, setCreatingPartnerTransfer] = useState(false);

  // Driver management states
  const [driverSearch, setDriverSearch] = useState("");
  const [creatingDriver, setCreatingDriver] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [deletingDriverId, setDeletingDriverId] = useState(null);
  const [changingDriverPassword, setChangingDriverPassword] = useState(null);
  const [driverVehicleTypeFilter, setDriverVehicleTypeFilter] = useState("all");

  const ITEMS_PER_PAGE = 5;

  // Season definitions
  const seasons = {
    low: {
      ranges: [
        { start: "2025-04-01", end: "2025-06-25" },
        { start: "2025-09-16", end: "2025-10-30" },
      ],
      name: "Low Season",
      description: "April 1 - June 25, September 16 - October 30",
    },
    medium: {
      ranges: [
        { start: "2025-06-26", end: "2025-07-10" },
        { start: "2025-08-26", end: "2025-09-15" },
      ],
      name: "Medium Season",
      description: "June 26 - July 10, August 26 - September 15",
    },
    high: {
      ranges: [{ start: "2025-07-11", end: "2025-08-25" }],
      name: "High Season",
      description: "July 11 - August 25",
    },
  };

  const handleSeasonSelect = async (carId, basePrice, seasonKey) => {
    const season = seasons[seasonKey];

    try {
      setSettingSeasonalPrice({ carId, season: seasonKey });
      const result = await Swal.fire({
        title: `Set ${season.name} Prices`,
        html: `
          <div class="mb-4">
            <p class="text-sm text-gray-600">${season.description}</p>
          </div>
          <div class="form-group">
            <label class="block text-sm font-medium text-gray-700 mb-1">Price per day (€)</label>
            <input 
              id="seasonPrice" 
              type="number" 
              class="w-full px-3 py-2 border rounded-md"
              value="${basePrice}"
              min="1"
              step="any"
            >
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: "Set Prices",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#1e3a8a", // blue-900
        preConfirm: () => {
          const price = document.getElementById("seasonPrice").value;
          if (!price || price <= 0) {
            Swal.showValidationMessage("Please enter a valid price");
            return false;
          }
          return price;
        },
      });

      if (result.isConfirmed) {
        const price = parseFloat(result.value);

        // Show loading state with progress
        const loadingAlert = Swal.fire({
          title: "Setting Seasonal Prices",
          html: "Processing dates...",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        // Process each date range for the season
        for (const range of season.ranges) {
          const dates = [];
          let currentDate = moment
            .tz(range.start, "Europe/Athens")
            .startOf("day");
          const endDate = moment.tz(range.end, "Europe/Athens").startOf("day");

          while (currentDate.isSameOrBefore(endDate)) {
            dates.push({
              date: currentDate.toDate(),
              isAvailable: true,
              dynamicPrice: price,
            });
            currentDate = currentDate.clone().add(1, "day");
          }

          // Update all dates in the range
          await Promise.all(
            dates.map((dateData) => cars.updateAvailability(carId, dateData))
          );
        }

        await loadingAlert.close();
        await Swal.fire({
          icon: "success",
          title: "Prices Updated",
          text: `${season.name} prices have been set to €${price} per day`,
          confirmButtonColor: "#1e3a8a",
        });

        fetchData(); // Refresh data
      }
    } catch (error) {
      console.error("Error setting seasonal prices:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update seasonal prices. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setSettingSeasonalPrice(null);
    }
  };

  const handleCarDelete = async (carId) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this! All related rentals will be kept for record keeping.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#1e3a8a",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        setDeletingCarId(carId);
        await cars.delete(carId);
        await Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "The car has been deleted.",
          confirmButtonColor: "#1e3a8a",
        });
        fetchData();
      }
    } catch (error) {
      console.error("Error deleting car:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete car. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setDeletingCarId(null);
    }
  };

  // set partners from partners.getAll();
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await partners.getAll();
        setPartners(response.data);
      } catch (error) {
        console.error("Error fetching partners:", error);
        setError("Failed to load partners data");
      }
    };
    fetchPartners();
  }, []);

  const handleRentalDelete = async (rentalId) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to recover this rental record!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#1e3a8a",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        setDeletingRentalId(rentalId);
        await rentals.delete(rentalId);
        await Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "The rental record has been deleted.",
          confirmButtonColor: "#1e3a8a",
        });
        fetchData();
      }
    } catch (error) {
      console.error("Error deleting rental:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete rental. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setDeletingRentalId(null);
    }
  };

  const handlePaymentStatusUpdate = async (rentalId, isPaid) => {
    try {
      setUpdatingPaymentId(rentalId);
      await rentals.updatePaymentStatus(rentalId, isPaid);
      await fetchData();
      await Swal.fire({
        icon: "success",
        title: "Success",
        text: `Payment status updated to ${isPaid ? "Paid" : "Not Paid"}`,
        confirmButtonColor: "#1e3a8a",
      });
    } catch (error) {
      console.error("Error updating payment status:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update payment status. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setUpdatingPaymentId(null);
    }
  };

  const getImagePath = (imagePath) => {
    if (!imagePath) return null;

    // If it's a relative path starting with /images/, replace it to point to assets
    if (imagePath.startsWith("/images/")) {
      const fileName = imagePath.split("/").pop();
      // Try to import from assets/images
      try {
        return require(`../assets/images/${fileName}`);
      } catch (e) {
        console.warn(`Could not find image: ${fileName} in assets folder`);
        return null;
      }
    }
    return imagePath;
  };

  const getDurationDiscount = (days) => {
    if (days > 10) return { percentage: 10, label: "Long-term Discount" };
    if (days > 3) return { percentage: 5, label: "Extended Stay Discount" };
    return null;
  };

  const filterCars = (cars) => {
    return cars.filter((car) => {
      const categoryMatch =
        selectedCategory === "all" || car.category === selectedCategory;
      const transmissionMatch =
        selectedTransmission === "all" ||
        car.transmission.toLowerCase() === selectedTransmission.toLowerCase();
      const searchMatch =
        searchQuery === "" ||
        car.make.toLowerCase().includes(searchQuery.toLowerCase()) ||
        car.model.toLowerCase().includes(searchQuery.toLowerCase());
      return categoryMatch && transmissionMatch && searchMatch;
    });
  };

  const handleDurationDiscountUpdate = async (term, value) => {
    try {
      setDurationDiscounts((prev) => ({
        ...prev,
        [term]: { ...prev[term], discount: parseInt(value) },
      }));

      await rentals.updateDurationDiscount(term, parseInt(value));
      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Duration discount updated successfully!",
        confirmButtonColor: "#1e3a8a",
      });
    } catch (error) {
      console.error("Error updating duration discount:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update duration discount. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
      // Revert the state on error
      fetchDurationDiscounts();
    }
  };

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const token =
          localStorage.getItem("adminToken") ||
          localStorage.getItem("Authentication:");
        if (!token) {
          throw new Error("No token found");
        }
        await auth.getCurrentUser();
        fetchData();
        fetchDurationDiscounts();
      } catch (error) {
        console.error("Authentication failed:", error);
        navigate("/admin/login");
      }
    };

    verifyAuth();
  }, [navigate]);
  const fetchData = async () => {
    try {
      const [
        carsRes,
        rentalsRes,
        couponsRes,
        transfersRes,
        driversRes,
        toursRes,
        tourBookingsRes,
      ] = await Promise.all([
        cars.getAll(),
        rentals.getAll(),
        coupons.getAll(),
        contact.getAllTransfers(),
        drivers.getAllDrivers(),
        tours.getAll(),
        tourBookings.getAll(),
      ]);

      // Process seasonal pricing data
      const seasonalPricingData = {};
      carsRes.data.forEach((car) => {
        if (car.availability && car.availability.length > 0) {
          seasonalPricingData[car._id] = car.availability;
        }
      });

      setCarsData(carsRes.data);
      setRentalsData(rentalsRes.data);
      setCouponsData(couponsRes.data);
      setTransfersData(transfersRes.data);
      setDriversData(driversRes.data);
      setToursData(toursRes.data);
      setTourBookingsData(tourBookingsRes.data);
      setSeasonalPricing(seasonalPricingData);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load dashboard data");
      setIsLoading(false);
    }
  };

  const fetchDurationDiscounts = async () => {
    try {
      const response = await rentals.getDurationDiscounts();
      setDurationDiscounts(response.data);
    } catch (error) {
      console.error("Error fetching duration discounts:", error);
    }
  };

  const handleCarAvailabilityUpdate = async (carId, availability) => {
    try {
      await cars.updateAvailability(carId, { availability });
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Error updating car availability:", error);
    }
  };

  const handleCarUpdate = async (carId, data) => {
    try {
      setUpdatingCarId(carId);
      await cars.update(carId, data);
      fetchData();
      setEditingCar(null);
    } catch (error) {
      console.error("Error updating car:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update car details. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setUpdatingCarId(null);
    }
  };

  const handleRentalStatusUpdate = async (rentalId, status) => {
    try {
      setUpdatingRentalId(rentalId);
      await rentals.updateStatus(rentalId, status);
      fetchData();
    } catch (error) {
      console.error("Error updating rental status:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update rental status. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setUpdatingRentalId(null);
    }
  };

  const handleCouponCreate = async (couponData) => {
    try {
      setCreatingCoupon(true);
      await coupons.create(couponData);
      fetchData();
      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Coupon created successfully!",
        confirmButtonColor: "#1e3a8a",
      });
    } catch (error) {
      console.error("Error creating coupon:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to create coupon. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setCreatingCoupon(false);
    }
  };

  const handleCouponDelete = async (couponId) => {
    try {
      setDeletingCouponId(couponId);
      await coupons.delete(couponId);
      fetchData();
    } catch (error) {
      console.error("Error deleting coupon:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete coupon. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setDeletingCouponId(null);
    }
  };

  const handleSeasonalPriceAdd = async (carId, data) => {
    try {
      await cars.updateAvailability(carId, {
        date: data.date,
        isAvailable: true,
        dynamicPrice: data.price,
      });
      fetchData();
    } catch (error) {
      console.error("Error updating seasonal price:", error);
    }
  };

  const handleSeasonalPriceRangeAdd = async (
    carId,
    { startDate, endDate, price }
  ) => {
    try {
      const dates = [];
      let currentDate = new Date(startDate);
      const end = new Date(endDate);

      while (currentDate <= end) {
        dates.push({
          date: new Date(currentDate),
          isAvailable: true,
          dynamicPrice: price,
        });
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Update all dates in range
      for (const dateData of dates) {
        await cars.updateAvailability(carId, dateData);
      }

      fetchData();
    } catch (error) {
      console.error("Error updating seasonal price range:", error);
    }
  };

  const handleCreateCar = async (formData) => {
    try {
      setIsCreatingCar(true);

      // formData is already a FormData object properly formatted from CreateCarModal
      // No need to recreate it or reformat the data
      await cars.create(formData);

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Car created successfully!",
        confirmButtonColor: "#1e3a8a",
      });

      fetchData();
      setCreateCarModalOpen(false);
    } catch (error) {
      console.error("Error creating car:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to create car. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setIsCreatingCar(false);
    }
  };

  const handleCouponEdit = async (couponData) => {
    try {
      await coupons.update(editingCoupon._id, couponData);
      setEditingCoupon(null);
      fetchData();
      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Coupon updated successfully!",
        confirmButtonColor: "#1e3a8a",
      });
    } catch (error) {
      console.error("Error updating coupon:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update coupon. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    }
  };

  const formatDateTime = (dateTime) => {
    return moment(dateTime).format("DD/MM HH:mm");
  };

  const formatPrice = (amount) => {
    return `€${amount.toFixed(2)}`;
  };

  const handleEditCar = (car) => {
    setSelectedCar(car);
    setCarDetailsModalOpen(true);
  };

  const filteredRentals = rentalsData.filter((rental) => {
    const matchesSearch =
      rental.customer.firstName
        .toLowerCase()
        .includes(rentalSearch.toLowerCase()) ||
      rental.customer.lastName
        .toLowerCase()
        .includes(rentalSearch.toLowerCase()) ||
      rental.carName.toLowerCase().includes(rentalSearch.toLowerCase()) ||
      rental.pickupLocation
        .toLowerCase()
        .includes(rentalSearch.toLowerCase()) ||
      rental.returnLocation.toLowerCase().includes(rentalSearch.toLowerCase());

    const matchesStatus =
      rentalStatusFilter === "all" || rental.status === rentalStatusFilter;

    const matchesDate =
      !dateFilter.startDate ||
      !dateFilter.endDate ||
      (new Date(rental.pickupDateTime) >= dateFilter.startDate &&
        new Date(rental.returnDateTime) <= dateFilter.endDate);

    return matchesSearch && matchesStatus && matchesDate;
  });

  const getDailyTurnover = () => {
    const today = moment().startOf("day");
    const pickups = rentalsData.filter(
      (rental) =>
        moment(rental.pickupDateTime).isSame(today, "day") &&
        rental.status !== "cancelled"
    );
    const returns = rentalsData.filter(
      (rental) =>
        moment(rental.returnDateTime).isSame(today, "day") &&
        rental.status !== "cancelled"
    );

    // Paginate pickups and returns
    const paginatedPickups = pickups.slice(
      (turnoverPage.pickups - 1) * ITEMS_PER_PAGE,
      turnoverPage.pickups * ITEMS_PER_PAGE
    );
    const paginatedReturns = returns.slice(
      (turnoverPage.returns - 1) * ITEMS_PER_PAGE,
      turnoverPage.returns * ITEMS_PER_PAGE
    );

    return {
      pickups: paginatedPickups,
      returns: paginatedReturns,
      totalPickups: pickups.length,
      totalReturns: returns.length,
    };
  };

  const getTomorrowTurnover = () => {
    const tomorrow = moment().add(1, "day").startOf("day");
    const pickups = rentalsData.filter(
      (rental) =>
        moment(rental.pickupDateTime).isSame(tomorrow, "day") &&
        rental.status !== "cancelled"
    );
    const returns = rentalsData.filter(
      (rental) =>
        moment(rental.returnDateTime).isSame(tomorrow, "day") &&
        rental.status !== "cancelled"
    );
    return {
      pickups,
      returns,
      totalPickups: pickups.length,
      totalReturns: returns.length,
    };
  };

  let isTomorrow = false;

  // Transfer management functions
  const handleTransferStatusUpdate = async (transferId, status) => {
    try {
      setUpdatingTransferStatus(transferId);
      await contact.updateTransferStatus(transferId, status);
      fetchData();
      await Swal.fire({
        icon: "success",
        title: "Success",
        text: `Transfer status updated to ${status}`,
        confirmButtonColor: "#1e3a8a",
      });
    } catch (error) {
      console.error("Error updating transfer status:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update transfer status. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setUpdatingTransferStatus(null);
    }
  };

  const handleTransferDelete = async (transferId) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to recover this transfer record!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#1e3a8a",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        setDeletingTransferId(transferId);
        await contact.deleteTransfer(transferId);
        await Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "The transfer record has been deleted.",
          confirmButtonColor: "#1e3a8a",
        });
        fetchData();
      }
    } catch (error) {
      console.error("Error deleting transfer:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete transfer. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setDeletingTransferId(null);
    }
  };

  const handleAssignDriverToTransfer = async (transferId) => {
    try {
      setAssigningDriverToTransfer(transferId);

      // Find transfer details
      const transfer = transfersData.find((t) => t._id === transferId);
      if (!transfer) {
        throw new Error("Transfer not found");
      }

      // Filter drivers by vehicle type compatibility
      const compatibleDrivers = driversData.filter(
        (driver) =>
          driver.vehicleTypes.includes(transfer.carType) && driver.isActive
      );

      if (compatibleDrivers.length === 0) {
        await Swal.fire({
          icon: "warning",
          title: "No Compatible Drivers",
          text: `No active drivers available for vehicle type: ${transfer.carType}`,
          confirmButtonColor: "#1e3a8a",
        });
        return;
      }

      // Create driver selection options
      const driverOptions = compatibleDrivers.map((driver) => {
        return {
          value: driver._id,
          text: `${driver.name} ${driver.surname} (${driver.phone})`,
        };
      });

      // Create a dropdown with the options
      const { value: driverId, dismiss } = await Swal.fire({
        title: "Assign Driver",
        html: `
          <div class="mb-3">
            <p class="text-sm text-left mb-1">Transfer: ${
              transfer.pickupLocation
            } → ${transfer.dropoffLocation}</p>
            <p class="text-sm text-left">Date: ${moment(
              transfer.pickUpDate
            ).format("DD/MM/YYYY")} at ${transfer.pickupHours}:${
          transfer.pickupMinutes
        }</p>
          </div>
          <div class="mb-3">
            <label for="driverSelect" class="block text-sm font-medium text-left mb-1">Select Driver:</label>
            <select id="driverSelect" class="w-full px-3 py-2 border rounded-md">
              ${driverOptions
                .map(
                  (option) =>
                    `<option value="${option.value}">${option.text}</option>`
                )
                .join("")}
            </select>
          </div>
          <div class="mb-3">
            <label for="driverNotes" class="block text-sm font-medium text-left mb-1">Notes for driver:</label>
            <textarea id="driverNotes" class="w-full px-3 py-2 border rounded-md" placeholder="Special instructions for the driver..."></textarea>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: "Assign",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#1e3a8a",
        preConfirm: () => {
          const driverId = document.getElementById("driverSelect").value;
          const driverNotes = document.getElementById("driverNotes").value;
          return { driverId, driverNotes };
        },
      });

      if (dismiss) return;

      // Assign the driver
      await drivers.assignDriver(
        transferId,
        driverId.driverId,
        driverId.driverNotes
      );

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Driver assigned successfully",
        confirmButtonColor: "#1e3a8a",
      });

      fetchData();
    } catch (error) {
      console.error("Error assigning driver:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to assign driver. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setAssigningDriverToTransfer(null);
    }
  };
  const handleUnassignDriver = async (transferId) => {
    try {
      const result = await Swal.fire({
        title: "Unassign Driver",
        text: "Are you sure you want to unassign the driver from this transfer?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#1e3a8a",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, unassign",
      });

      if (result.isConfirmed) {
        await drivers.unassignDriver(transferId);
        await Swal.fire({
          icon: "success",
          title: "Success",
          text: "Driver unassigned successfully",
          confirmButtonColor: "#1e3a8a",
        });
        fetchData();
      }
    } catch (error) {
      console.error("Error unassigning driver:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to unassign driver. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    }
  };

  // Handler for creating a new transfer
  const handleCreateTransfer = async () => {
    try {
      // Get vehicle type options
      const vehicleTypeOptions = [
        { value: "car", label: "Car up to 2 pax" },
        { value: "minivan", label: "Mini Van up to 7 pax" },
        { value: "van", label: "Van up to 12 pax" },
        { value: "minibus", label: "Mini Bus up to 20 pax" },
      ];

      // Get location options from transfer pricing data
      const locationOptions = [];
      const transferPricings = await transferPricing.getAll();
      if (transferPricings && transferPricings.data) {
        // Extract unique pickup and dropoff locations
        const uniqueFromLocations = new Set();
        const uniqueToLocations = new Set();

        transferPricings.data.forEach((pricing) => {
          uniqueFromLocations.add(pricing.fromLocation);
          uniqueToLocations.add(pricing.toLocation);
        });

        // Convert to options format
        uniqueFromLocations.forEach((location) => {
          locationOptions.push({ value: location, label: location });
        });
        uniqueToLocations.forEach((location) => {
          if (!locationOptions.some((opt) => opt.value === location)) {
            locationOptions.push({ value: location, label: location });
          }
        });
      }

      // Get season for date calculation
      const getSeason = (date) => {
        const month = date.getMonth() + 1; // JavaScript months are 0-based
        const day = date.getDate();

        // Low Season: April 1 - June 25, September 16 - October 30
        if (
          (month === 4 && day >= 1) ||
          month === 5 ||
          (month === 6 && day <= 25) ||
          (month === 9 && day >= 16) ||
          (month === 10 && day <= 30)
        ) {
          return "low";
        }
        // Medium Season: June 26 - July 10, August 26 - September 15
        else if (
          (month === 6 && day >= 26) ||
          (month === 7 && day <= 10) ||
          (month === 8 && day >= 26) ||
          (month === 9 && day <= 15)
        ) {
          return "medium";
        }
        // High Season: July 11 - August 25
        else if ((month === 7 && day >= 11) || (month === 8 && day <= 25)) {
          return "high";
        }
        // Default to medium season
        else {
          return "medium";
        }
      };

      // Calculate price based on route and season
      const calculateTransferPrice = (
        fromLocation,
        toLocation,
        season,
        carType
      ) => {
        // Find the pricing record for this route
        const pricingRecord = transferPricings.data.find(
          (pricing) =>
            (pricing.fromLocation === fromLocation &&
              pricing.toLocation === toLocation) ||
            (pricing.fromLocation === toLocation &&
              pricing.toLocation === fromLocation)
        );

        if (!pricingRecord) return null;

        let basePrice = 0;
        switch (season) {
          case "low":
            basePrice = pricingRecord.lowSeasonPrice;
            break;
          case "medium":
            basePrice = pricingRecord.mediumSeasonPrice;
            break;
          case "high":
            basePrice = pricingRecord.highSeasonPrice;
            break;
          default:
            basePrice = pricingRecord.mediumSeasonPrice;
        }

        return Math.round(basePrice);
      };

      const { value: formValues, dismiss: transferDismiss } = await Swal.fire({
        title: "Create New Transfer",
        html: `
          <div class="grid grid-cols-1 gap-4 max-h-[70vh] overflow-y-auto p-2">
            <!-- Customer Information -->
            <div class="p-4 bg-gray-50 rounded-lg">
              <h3 class="font-semibold text-lg mb-3 text-left">Customer Information</h3>
              <div class="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label for="name" class="block text-sm font-medium text-left mb-1">First Name*</label>
                  <input id="name" type="text" class="w-full px-3 py-2 border rounded-md" required>
                </div>
                <div>
                  <label for="surname" class="block text-sm font-medium text-left mb-1">Last Name*</label>
                  <input id="surname" type="text" class="w-full px-3 py-2 border rounded-md" required>
                </div>
              </div>
              <div class="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label for="email" class="block text-sm font-medium text-left mb-1">Email*</label>
                  <input id="email" type="email" class="w-full px-3 py-2 border rounded-md" required>
                </div>
                <div>
                  <label for="phone" class="block text-sm font-medium text-left mb-1">Phone*</label>
                  <input id="phone" type="text" class="w-full px-3 py-2 border rounded-md" required>
                </div>
              </div>
            </div>
            
            <!-- Transfer Details -->
            <div class="p-4 bg-gray-50 rounded-lg">
              <h3 class="font-semibold text-lg mb-3 text-left">Transfer Details</h3>
              <div class="mb-3">
                <label for="carType" class="block text-sm font-medium text-left mb-1">Vehicle Type*</label>
                <select id="carType" class="w-full px-3 py-2 border rounded-md" required>
                  <option value="">Select vehicle type</option>
                  ${vehicleTypeOptions
                    .map(
                      (opt) =>
                        `<option value="${opt.value}">${opt.label}</option>`
                    )
                    .join("")}
                </select>
              </div>
              <div class="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label for="persons" class="block text-sm font-medium text-left mb-1">Passengers*</label>
                  <input id="persons" type="number" min="1" class="w-full px-3 py-2 border rounded-md" required>
                </div>
                <div>
                  <label for="luggage" class="block text-sm font-medium text-left mb-1">Luggage*</label>
                  <input id="luggage" type="number" min="0" class="w-full px-3 py-2 border rounded-md" required>
                </div>
              </div>
              <div class="mb-3">
                <label for="serviceType" class="block text-sm font-medium text-left mb-1">Service Type*</label>
                <select id="serviceType" class="w-full px-3 py-2 border rounded-md" required>
                  <option value="one-way">One Way</option>
                  <option value="round-trip">Round Trip</option>
                </select>
              </div>
            </div>
            
            <!-- Locations and Dates -->
            <div class="p-4 bg-gray-50 rounded-lg">
              <h3 class="font-semibold text-lg mb-3 text-left">Locations and Dates</h3>
              <div class="mb-3">
                <label for="pickupLocation" class="block text-sm font-medium text-left mb-1">Pickup Location*</label>
                <select id="pickupLocation" class="w-full px-3 py-2 border rounded-md" required>
                  <option value="">Select pickup location</option>
                  ${locationOptions
                    .map(
                      (opt) =>
                        `<option value="${opt.value}">${opt.label}</option>`
                    )
                    .join("")}
                  <option value="other">Other (Custom)</option>
                </select>
              </div>
              <div id="otherPickupContainer" class="mb-3 hidden">
                <label for="otherPickupLocation" class="block text-sm font-medium text-left mb-1">Custom Pickup Location*</label>
                <input id="otherPickupLocation" type="text" class="w-full px-3 py-2 border rounded-md">
              </div>
              
              <div class="mb-3">
                <label for="dropoffLocation" class="block text-sm font-medium text-left mb-1">Drop-off Location*</label>
                <select id="dropoffLocation" class="w-full px-3 py-2 border rounded-md" required>
                  <option value="">Select drop-off location</option>
                  ${locationOptions
                    .map(
                      (opt) =>
                        `<option value="${opt.value}">${opt.label}</option>`
                    )
                    .join("")}
                  <option value="other">Other (Custom)</option>
                </select>
              </div>
              <div id="otherDropoffContainer" class="mb-3 hidden">
                <label for="otherDropoffLocation" class="block text-sm font-medium text-left mb-1">Custom Drop-off Location*</label>
                <input id="otherDropoffLocation" type="text" class="w-full px-3 py-2 border rounded-md">
              </div>
              
              <div class="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label for="pickupDate" class="block text-sm font-medium text-left mb-1">Pickup Date*</label>
                  <input id="pickupDate" type="date" class="w-full px-3 py-2 border rounded-md" min="${moment().format(
                    "YYYY-MM-DD"
                  )}" required>
                </div>
                <div>
                  <label for="pickupTime" class="block text-sm font-medium text-left mb-1">Pickup Time*</label>
                  <input id="pickupTime" type="time" class="w-full px-3 py-2 border rounded-md" required>
                </div>
              </div>
              
              <div id="returnDetailsContainer" class="hidden">
                <div class="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label for="returnDate" class="block text-sm font-medium text-left mb-1">Return Date*</label>
                    <input id="returnDate" type="date" class="w-full px-3 py-2 border rounded-md" min="${moment().format(
                      "YYYY-MM-DD"
                    )}">
                  </div>
                  <div>
                    <label for="returnTime" class="block text-sm font-medium text-left mb-1">Return Time*</label>
                    <input id="returnTime" type="time" class="w-full px-3 py-2 border rounded-md">
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Additional Information -->
            <div class="p-4 bg-gray-50 rounded-lg">
              <h3 class="font-semibold text-lg mb-3 text-left">Additional Options</h3>
              <div class="mb-3">
                <label for="paymentMethod" class="block text-sm font-medium text-left mb-1">Payment Method*</label>
                <select id="paymentMethod" class="w-full px-3 py-2 border rounded-md" required>
                  <option value="credit-card">Credit Card</option>
                  <option value="cash">Cash</option>
                  <option value="bank-transfer">Bank Transfer</option>
                </select>
              </div>
              <div class="mb-3">
                <label for="airportTicketId" class="block text-sm font-medium text-left mb-1">Flight/Ferry Number</label>
                <input id="airportTicketId" type="text" class="w-full px-3 py-2 border rounded-md">
              </div>
              <div class="mb-3">
                <div class="flex items-center">
                  <input id="atDisposal" type="checkbox" class="mr-2">
                  <label for="atDisposal" class="text-sm font-medium">Driver at Disposal</label>
                </div>
              </div>
              <div id="disposalHoursContainer" class="mb-3 hidden">
                <label for="disposalHours" class="block text-sm font-medium text-left mb-1">Disposal Hours</label>
                <select id="disposalHours" class="w-full px-3 py-2 border rounded-md">
                  ${Array.from({ length: 12 }, (_, i) => i + 1)
                    .map(
                      (h) =>
                        `<option value="${h}">${h} hour${
                          h > 1 ? "s" : ""
                        }</option>`
                    )
                    .join("")}
                </select>
              </div>
              <div class="mb-3">
                <div class="flex items-center">
                  <input id="helicopterTransfer" type="checkbox" class="mr-2">
                  <label for="helicopterTransfer" class="text-sm font-medium">Helicopter Transfer</label>
                </div>
              </div>
              <div class="mb-3">
                <label for="additionalInfo" class="block text-sm font-medium text-left mb-1">Additional Comments</label>
                <textarea id="additionalInfo" class="w-full px-3 py-2 border rounded-md" rows="3"></textarea>
              </div>
            </div>
          </div>
        `,
        didOpen: () => {
          // Add event listeners for conditional fields
          document
            .getElementById("serviceType")
            .addEventListener("change", function () {
              document.getElementById("returnDetailsContainer").style.display =
                this.value === "round-trip" ? "block" : "none";
            });

          document
            .getElementById("pickupLocation")
            .addEventListener("change", function () {
              document.getElementById("otherPickupContainer").style.display =
                this.value === "other" ? "block" : "none";

              // Update pricing
              updatePriceEstimate();
            });

          document
            .getElementById("dropoffLocation")
            .addEventListener("change", function () {
              document.getElementById("otherDropoffContainer").style.display =
                this.value === "other" ? "block" : "none";

              // Update pricing
              updatePriceEstimate();
            });

          document
            .getElementById("carType")
            .addEventListener("change", function () {
              // Update pricing
              updatePriceEstimate();
            });

          document
            .getElementById("pickupDate")
            .addEventListener("change", function () {
              // Update pricing
              updatePriceEstimate();
            });

          document
            .getElementById("atDisposal")
            .addEventListener("change", function () {
              document.getElementById("disposalHoursContainer").style.display =
                this.checked ? "block" : "none";
            });

          // Function to update price estimate
          function updatePriceEstimate() {
            const pickupLocation =
              document.getElementById("pickupLocation").value;
            const dropoffLocation =
              document.getElementById("dropoffLocation").value;
            const pickupDate = document.getElementById("pickupDate").value;
            const carType = document.getElementById("carType").value;

            // Only calculate if all fields are filled
            if (
              pickupLocation &&
              pickupLocation !== "other" &&
              dropoffLocation &&
              dropoffLocation !== "other" &&
              pickupDate &&
              carType
            ) {
              const season = getSeason(new Date(pickupDate));
              const price = calculateTransferPrice(
                pickupLocation,
                dropoffLocation,
                season,
                carType
              );

              if (price) {
                // If a price estimate exists, display it somewhere
                let priceEstimateEl = document.getElementById("priceEstimate");
                if (!priceEstimateEl) {
                  priceEstimateEl = document.createElement("div");
                  priceEstimateEl.id = "priceEstimate";
                  priceEstimateEl.className =
                    "mt-4 p-3 bg-blue-50 text-blue-800 rounded-md";
                  document
                    .querySelector(".swal2-html-container")
                    .appendChild(priceEstimateEl);
                }

                priceEstimateEl.innerHTML = `
                  <p class="font-medium">Estimated Price: €${price}</p>
                  <p class="text-xs">${
                    season.charAt(0).toUpperCase() + season.slice(1)
                  } Season Rate</p>
                `;
              }
            }
          }
        },
        showCancelButton: true,
        confirmButtonText: "Create Transfer",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#1e3a8a",
        width: "800px",
        preConfirm: () => {
          // Get form values
          const name = document.getElementById("name").value;
          const surname = document.getElementById("surname").value;
          const email = document.getElementById("email").value;
          const phone = document.getElementById("phone").value;
          const carType = document.getElementById("carType").value;
          const persons = document.getElementById("persons").value;
          const luggage = document.getElementById("luggage").value;
          const serviceType = document.getElementById("serviceType").value;
          const pickupLocation =
            document.getElementById("pickupLocation").value;
          const otherPickupLocation = document.getElementById(
            "otherPickupLocation"
          ).value;
          const dropoffLocation =
            document.getElementById("dropoffLocation").value;
          const otherDropoffLocation = document.getElementById(
            "otherDropoffLocation"
          ).value;
          const pickupDate = document.getElementById("pickupDate").value;
          const pickupTime = document.getElementById("pickupTime").value;
          const returnDate = document.getElementById("returnDate")?.value;
          const returnTime = document.getElementById("returnTime")?.value;
          const paymentMethod = document.getElementById("paymentMethod").value;
          const airportTicketId =
            document.getElementById("airportTicketId").value;
          const atDisposal = document.getElementById("atDisposal").checked;
          const disposalHours = document.getElementById("disposalHours").value;
          const helicopterTransfer =
            document.getElementById("helicopterTransfer").checked;
          const additionalInfo =
            document.getElementById("additionalInfo").value;

          // Validate required fields
          if (
            !name ||
            !surname ||
            !email ||
            !phone ||
            !carType ||
            !persons ||
            !luggage ||
            !pickupLocation ||
            !dropoffLocation ||
            !pickupDate ||
            !pickupTime
          ) {
            Swal.showValidationMessage("Please fill all required fields");
            return false;
          }

          if (pickupLocation === "other" && !otherPickupLocation) {
            Swal.showValidationMessage("Please enter a custom pickup location");
            return false;
          }

          if (dropoffLocation === "other" && !otherDropoffLocation) {
            Swal.showValidationMessage(
              "Please enter a custom drop-off location"
            );
            return false;
          }

          if (serviceType === "round-trip" && (!returnDate || !returnTime)) {
            Swal.showValidationMessage("Please enter return date and time");
            return false;
          }

          // Parse dates and times
          const [pickupHours, pickupMinutes] = pickupTime.split(":");
          const pickUpDateObj = new Date(pickupDate);
          pickUpDateObj.setHours(
            parseInt(pickupHours),
            parseInt(pickupMinutes)
          );

          let returnDateObj = null;
          let returnHours = null;
          let returnMinutes = null;

          if (serviceType === "round-trip" && returnDate && returnTime) {
            [returnHours, returnMinutes] = returnTime.split(":");
            returnDateObj = new Date(returnDate);
            returnDateObj.setHours(
              parseInt(returnHours),
              parseInt(returnMinutes)
            );

            if (returnDateObj < pickUpDateObj) {
              Swal.showValidationMessage(
                "Return date cannot be before pickup date"
              );
              return false;
            }
          }

          // Calculate season based on date
          const season = getSeason(pickUpDateObj);

          // Calculate price if standard locations are selected
          let price = null;
          if (pickupLocation !== "other" && dropoffLocation !== "other") {
            price = calculateTransferPrice(
              pickupLocation,
              dropoffLocation,
              season,
              carType
            );
          }

          // Create final form data
          return {
            name,
            surname,
            email,
            phone,
            carType,
            persons,
            luggage,
            serviceType,
            pickupLocation:
              pickupLocation === "other" ? otherPickupLocation : pickupLocation,
            dropoffLocation:
              dropoffLocation === "other"
                ? otherDropoffLocation
                : dropoffLocation,
            pickUpDate: pickUpDateObj,
            pickupHours,
            pickupMinutes,
            dropOffDate: pickUpDateObj, // For one-way, drop-off date is same as pickup
            dropoffHours: pickupHours,
            dropoffMinutes: pickupMinutes,
            returnDate: returnDateObj,
            returnHours,
            returnMinutes,
            paymentMethod,
            airportTicketId,
            atDisposal,
            disposalHours: atDisposal ? disposalHours : null,
            helicopterTransfer,
            additionalInfo,
            status: "pending",
            isReturn: serviceType === "round-trip",
            seasonType: season,
            price: price,
          };
        },
      });

      if (transferDismiss) return;

      // Create the transfer
      await contact.submitTransferRequest(formValues);

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Transfer created successfully",
        confirmButtonColor: "#1e3a8a",
      });

      fetchData();
    } catch (error) {
      console.error("Error creating transfer:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to create transfer. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    }
  };

  // Handler for creating a partner transfer
  const handleCreatePartnerTransfer = async () => {
    try {
      setCreatingPartnerTransfer(true);

      // First select a partner and commission
      const { value: partnerData, dismiss: partnerDismiss } = await Swal.fire({
        title: "Select Partner",
        html: `
          <div class="mb-4">
        <label for="partner" class="block text-sm font-medium text-left mb-1">Partner*</label>
        <select id="partner" class="w-full px-3 py-2 border rounded-md" required>
          <option value="">Select a partner</option>
          ${partnersData
            .map(
              (p) =>
                `<option value="${p._id}">${p.firstName} ${p.lastName}</option>`
            )
            .join("")}
        </select>
          </div>
          <div class="mb-4">
        <label for="commission" class="block text-sm font-medium text-left mb-1">Partner Commission (€)</label>
        <input id="commission" type="number" min="0" step="0.01" class="w-full px-3 py-2 border rounded-md" value="0">
        <p class="text-xs text-gray-500 mt-1">Amount that will be paid to the partner</p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: "Continue",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#1e3a8a",
        preConfirm: () => {
          const partnerId = document.getElementById("partner").value;
          const commission = document.getElementById("commission").value;

          if (!partnerId) {
            Swal.showValidationMessage("Please select a partner");
            return false;
          }

          const selectedPartner = partnersData.find((p) => p._id === partnerId);

          return {
            partnerId,
            commission: parseFloat(commission) || 0,
            partner: selectedPartner,
          };
        },
      });

      if (partnerDismiss) {
        setCreatingPartnerTransfer(false);
        return;
      }

      // Get vehicle type options
      const vehicleTypeOptions = [
        { value: "car", label: "Car up to 2 pax" },
        { value: "minivan", label: "Mini Van up to 7 pax" },
        { value: "van", label: "Van up to 12 pax" },
        { value: "minibus", label: "Mini Bus up to 20 pax" },
      ];

      // Get location options from transfer pricing data
      const locationOptions = [];
      const transferPricings = await transferPricing.getAll();
      if (transferPricings && transferPricings.data) {
        // Extract unique pickup and dropoff locations
        const uniqueFromLocations = new Set();
        const uniqueToLocations = new Set();

        transferPricings.data.forEach((pricing) => {
          uniqueFromLocations.add(pricing.fromLocation);
          uniqueToLocations.add(pricing.toLocation);
        });

        // Convert to options format
        uniqueFromLocations.forEach((location) => {
          locationOptions.push({ value: location, label: location });
        });
        uniqueToLocations.forEach((location) => {
          if (!locationOptions.some((opt) => opt.value === location)) {
            locationOptions.push({ value: location, label: location });
          }
        });
      }

      // Get season for date calculation
      const getSeason = (date) => {
        const month = date.getMonth() + 1; // JavaScript months are 0-based
        const day = date.getDate();

        // Low Season: April 1 - June 25, September 16 - October 30
        if (
          (month === 4 && day >= 1) ||
          month === 5 ||
          (month === 6 && day <= 25) ||
          (month === 9 && day >= 16) ||
          (month === 10 && day <= 30)
        ) {
          return "low";
        }
        // Medium Season: June 26 - July 10, August 26 - September 15
        else if (
          (month === 6 && day >= 26) ||
          (month === 7 && day <= 10) ||
          (month === 8 && day >= 26) ||
          (month === 9 && day <= 15)
        ) {
          return "medium";
        }
        // High Season: July 11 - August 25
        else if ((month === 7 && day >= 11) || (month === 8 && day <= 25)) {
          return "high";
        }
        // Default to medium season
        else {
          return "medium";
        }
      };

      // Calculate price based on route and season
      const calculateTransferPrice = (
        fromLocation,
        toLocation,
        season,
        carType
      ) => {
        // Find the pricing record for this route
        const pricingRecord = transferPricings.data.find(
          (pricing) =>
            (pricing.fromLocation === fromLocation &&
              pricing.toLocation === toLocation) ||
            (pricing.fromLocation === toLocation &&
              pricing.toLocation === fromLocation)
        );

        if (!pricingRecord) return null;

        let basePrice = 0;
        switch (season) {
          case "low":
            basePrice = pricingRecord.lowSeasonPrice;
            break;
          case "medium":
            basePrice = pricingRecord.mediumSeasonPrice;
            break;
          case "high":
            basePrice = pricingRecord.highSeasonPrice;
            break;
          default:
            basePrice = pricingRecord.mediumSeasonPrice;
        }

        return Math.round(basePrice);
      };

      const { value: formValues, dismiss: transferDismiss } = await Swal.fire({
        title: `Create Transfer for ${partnerData.partner.firstName} ${partnerData.partner.lastName}`,
        html: `
          <div class="grid grid-cols-1 gap-4 max-h-[70vh] overflow-y-auto p-2">
            <!-- Customer Information -->
            <div class="p-4 bg-gray-50 rounded-lg">
              <h3 class="font-semibold text-lg mb-3 text-left">Customer Information</h3>
              <div class="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label for="name" class="block text-sm font-medium text-left mb-1">First Name*</label>
                  <input id="name" type="text" class="w-full px-3 py-2 border rounded-md" required>
                </div>
                <div>
                  <label for="surname" class="block text-sm font-medium text-left mb-1">Last Name*</label>
                  <input id="surname" type="text" class="w-full px-3 py-2 border rounded-md" required>
                </div>
              </div>
              <div class="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label for="email" class="block text-sm font-medium text-left mb-1">Email*</label>
                  <input id="email" type="email" class="w-full px-3 py-2 border rounded-md" required>
                </div>
                <div>
                  <label for="phone" class="block text-sm font-medium text-left mb-1">Phone*</label>
                  <input id="phone" type="text" class="w-full px-3 py-2 border rounded-md" required>
                </div>
              </div>
            </div>
            
            <!-- Transfer Details -->
            <div class="p-4 bg-gray-50 rounded-lg">
              <h3 class="font-semibold text-lg mb-3 text-left">Transfer Details</h3>
              <div class="mb-3">
                <label for="carType" class="block text-sm font-medium text-left mb-1">Vehicle Type*</label>
                <select id="carType" class="w-full px-3 py-2 border rounded-md" required>
                  <option value="">Select vehicle type</option>
                  ${vehicleTypeOptions
                    .map(
                      (opt) =>
                        `<option value="${opt.value}">${opt.label}</option>`
                    )
                    .join("")}
                </select>
              </div>
              <div class="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label for="persons" class="block text-sm font-medium text-left mb-1">Passengers*</label>
                  <input id="persons" type="number" min="1" class="w-full px-3 py-2 border rounded-md" required>
                </div>
                <div>
                  <label for="luggage" class="block text-sm font-medium text-left mb-1">Luggage*</label>
                  <input id="luggage" type="number" min="0" class="w-full px-3 py-2 border rounded-md" required>
                </div>
              </div>
              <div class="mb-3">
                <label for="serviceType" class="block text-sm font-medium text-left mb-1">Service Type*</label>
                <select id="serviceType" class="w-full px-3 py-2 border rounded-md" required>
                  <option value="one-way">One Way</option>
                  <option value="round-trip">Round Trip</option>
                </select>
              </div>
            </div>
            
            <!-- Locations and Dates -->
            <div class="p-4 bg-gray-50 rounded-lg">
              <h3 class="font-semibold text-lg mb-3 text-left">Locations and Dates</h3>
              <div class="mb-3">
                <label for="pickupLocation" class="block text-sm font-medium text-left mb-1">Pickup Location*</label>
                <select id="pickupLocation" class="w-full px-3 py-2 border rounded-md" required>
                  <option value="">Select pickup location</option>
                  ${locationOptions
                    .map(
                      (opt) =>
                        `<option value="${opt.value}">${opt.label}</option>`
                    )
                    .join("")}
                  <option value="other">Other (Custom)</option>
                </select>
              </div>
              <div id="otherPickupContainer" class="mb-3 hidden">
                <label for="otherPickupLocation" class="block text-sm font-medium text-left mb-1">Custom Pickup Location*</label>
                <input id="otherPickupLocation" type="text" class="w-full px-3 py-2 border rounded-md">
              </div>
              
              <div class="mb-3">
                <label for="dropoffLocation" class="block text-sm font-medium text-left mb-1">Drop-off Location*</label>
                <select id="dropoffLocation" class="w-full px-3 py-2 border rounded-md" required>
                  <option value="">Select drop-off location</option>
                  ${locationOptions
                    .map(
                      (opt) =>
                        `<option value="${opt.value}">${opt.label}</option>`
                    )
                    .join("")}
                  <option value="other">Other (Custom)</option>
                </select>
              </div>
              <div id="otherDropoffContainer" class="mb-3 hidden">
                <label for="otherDropoffLocation" class="block text-sm font-medium text-left mb-1">Custom Drop-off Location*</label>
                <input id="otherDropoffLocation" type="text" class="w-full px-3 py-2 border rounded-md">
              </div>
              
              <div class="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label for="pickupDate" class="block text-sm font-medium text-left mb-1">Pickup Date*</label>
                  <input id="pickupDate" type="date" class="w-full px-3 py-2 border rounded-md" min="${moment().format(
                    "YYYY-MM-DD"
                  )}" required>
                </div>
                <div>
                  <label for="pickupTime" class="block text-sm font-medium text-left mb-1">Pickup Time*</label>
                  <input id="pickupTime" type="time" class="w-full px-3 py-2 border rounded-md" required>
                </div>
              </div>
              
              <div id="returnDetailsContainer" class="hidden">
                <div class="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label for="returnDate" class="block text-sm font-medium text-left mb-1">Return Date*</label>
                    <input id="returnDate" type="date" class="w-full px-3 py-2 border rounded-md" min="${moment().format(
                      "YYYY-MM-DD"
                    )}">
                  </div>
                  <div>
                    <label for="returnTime" class="block text-sm font-medium text-left mb-1">Return Time*</label>
                    <input id="returnTime" type="time" class="w-full px-3 py-2 border rounded-md">
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Additional Information -->
            <div class="p-4 bg-gray-50 rounded-lg">
              <h3 class="font-semibold text-lg mb-3 text-left">Additional Options</h3>
              <div class="mb-3">
                <label for="paymentMethod" class="block text-sm font-medium text-left mb-1">Payment Method*</label>
                <select id="paymentMethod" class="w-full px-3 py-2 border rounded-md" required>
                  <option value="credit-card">Credit Card</option>
                  <option value="cash">Cash</option>
                  <option value="bank-transfer">Bank Transfer</option>
                </select>
              </div>
              <div class="mb-3">
                <label for="airportTicketId" class="block text-sm font-medium text-left mb-1">Flight/Ferry Number</label>
                <input id="airportTicketId" type="text" class="w-full px-3 py-2 border rounded-md">
              </div>
              <div class="mb-3">
                <div class="flex items-center">
                  <input id="atDisposal" type="checkbox" class="mr-2">
                  <label for="atDisposal" class="text-sm font-medium">Driver at Disposal</label>
                </div>
              </div>
              <div id="disposalHoursContainer" class="mb-3 hidden">
                <label for="disposalHours" class="block text-sm font-medium text-left mb-1">Disposal Hours</label>
                <select id="disposalHours" class="w-full px-3 py-2 border rounded-md">
                  ${Array.from({ length: 12 }, (_, i) => i + 1)
                    .map(
                      (h) =>
                        `<option value="${h}">${h} hour${
                          h > 1 ? "s" : ""
                        }</option>`
                    )
                    .join("")}
                </select>
              </div>
              <div class="mb-3">
                <div class="flex items-center">
                  <input id="helicopterTransfer" type="checkbox" class="mr-2">
                  <label for="helicopterTransfer" class="text-sm font-medium">Helicopter Transfer</label>
                </div>
              </div>
              <div class="mb-3">
                <label for="additionalInfo" class="block text-sm font-medium text-left mb-1">Additional Comments</label>
                <textarea id="additionalInfo" class="w-full px-3 py-2 border rounded-md" rows="3"></textarea>
              </div>
            </div>
            
            <!-- Partner Information -->
            <div class="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 class="font-semibold text-lg mb-3 text-left text-blue-800">
                Partner: ${partnerData.partner.firstName} ${
          partnerData.partner.lastName
        }
              </h3>
              <div class="mb-3">
                <p class="text-blue-600">Commission: €${
                  partnerData.commission
                }</p>
              </div>
            </div>
          </div>
        `,
        didOpen: () => {
          // Add event listeners for conditional fields
          document
            .getElementById("serviceType")
            .addEventListener("change", function () {
              document.getElementById("returnDetailsContainer").style.display =
                this.value === "round-trip" ? "block" : "none";
            });

          document
            .getElementById("pickupLocation")
            .addEventListener("change", function () {
              document.getElementById("otherPickupContainer").style.display =
                this.value === "other" ? "block" : "none";

              // Update pricing
              updatePriceEstimate();
            });

          document
            .getElementById("dropoffLocation")
            .addEventListener("change", function () {
              document.getElementById("otherDropoffContainer").style.display =
                this.value === "other" ? "block" : "none";

              // Update pricing
              updatePriceEstimate();
            });

          document
            .getElementById("carType")
            .addEventListener("change", function () {
              // Update pricing
              updatePriceEstimate();
            });

          document
            .getElementById("pickupDate")
            .addEventListener("change", function () {
              // Update pricing
              updatePriceEstimate();
            });

          document
            .getElementById("atDisposal")
            .addEventListener("change", function () {
              document.getElementById("disposalHoursContainer").style.display =
                this.checked ? "block" : "none";
            });

          // Function to update price estimate
          function updatePriceEstimate() {
            const pickupLocation =
              document.getElementById("pickupLocation").value;
            const dropoffLocation =
              document.getElementById("dropoffLocation").value;
            const pickupDate = document.getElementById("pickupDate").value;
            const carType = document.getElementById("carType").value;
            const serviceType = document.getElementById("serviceType").value;

            // Only calculate if all fields are filled
            if (
              pickupLocation &&
              pickupLocation !== "other" &&
              dropoffLocation &&
              dropoffLocation !== "other" &&
              pickupDate &&
              carType
            ) {
              const season = getSeason(new Date(pickupDate));
              let price = calculateTransferPrice(
                pickupLocation,
                dropoffLocation,
                season,
                carType
              );

              if (price) {
                // Double price for round trip
                if (serviceType === "round-trip") {
                  price = price * 2;
                }

                // If a price estimate exists, display it somewhere
                let priceEstimateEl = document.getElementById("priceEstimate");
                if (!priceEstimateEl) {
                  priceEstimateEl = document.createElement("div");
                  priceEstimateEl.id = "priceEstimate";
                  priceEstimateEl.className =
                    "mt-4 p-3 bg-blue-50 text-blue-800 rounded-md";
                  document
                    .querySelector(".swal2-html-container")
                    .appendChild(priceEstimateEl);
                }

                priceEstimateEl.innerHTML = `
                  <p class="font-medium">Estimated Price: €${price}</p>
                  <p class="text-xs">${
                    season.charAt(0).toUpperCase() + season.slice(1)
                  } Season Rate</p>
                  <p class="text-xs mt-1">Partner Commission: €${
                    partnerData.commission
                  }</p>
                `;
              }
            }
          }
        },
        showCancelButton: true,
        confirmButtonText: "Create Transfer",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#1e3a8a",
        width: "800px",
        preConfirm: () => {
          // Get form values
          const name = document.getElementById("name").value;
          const surname = document.getElementById("surname").value;
          const email = document.getElementById("email").value;
          const phone = document.getElementById("phone").value;
          const carType = document.getElementById("carType").value;
          const persons = document.getElementById("persons").value;
          const luggage = document.getElementById("luggage").value;
          const serviceType = document.getElementById("serviceType").value;
          const pickupLocation =
            document.getElementById("pickupLocation").value;
          const otherPickupLocation = document.getElementById(
            "otherPickupLocation"
          ).value;
          const dropoffLocation =
            document.getElementById("dropoffLocation").value;
          const otherDropoffLocation = document.getElementById(
            "otherDropoffLocation"
          ).value;
          const pickupDate = document.getElementById("pickupDate").value;
          const pickupTime = document.getElementById("pickupTime").value;
          const returnDate = document.getElementById("returnDate")?.value;
          const returnTime = document.getElementById("returnTime")?.value;
          const paymentMethod = document.getElementById("paymentMethod").value;
          const airportTicketId =
            document.getElementById("airportTicketId").value;
          const atDisposal = document.getElementById("atDisposal").checked;
          const disposalHours = document.getElementById("disposalHours").value;
          const helicopterTransfer =
            document.getElementById("helicopterTransfer").checked;
          const additionalInfo =
            document.getElementById("additionalInfo").value;

          // Validate required fields
          if (
            !name ||
            !surname ||
            !email ||
            !phone ||
            !carType ||
            !persons ||
            !luggage ||
            !pickupLocation ||
            !dropoffLocation ||
            !pickupDate ||
            !pickupTime
          ) {
            Swal.showValidationMessage("Please fill all required fields");
            return false;
          }

          if (pickupLocation === "other" && !otherPickupLocation) {
            Swal.showValidationMessage("Please enter a custom pickup location");
            return false;
          }

          if (dropoffLocation === "other" && !otherDropoffLocation) {
            Swal.showValidationMessage(
              "Please enter a custom drop-off location"
            );
            return false;
          }

          if (serviceType === "round-trip" && (!returnDate || !returnTime)) {
            Swal.showValidationMessage("Please enter return date and time");
            return false;
          }

          // Parse dates and times
          const [pickupHours, pickupMinutes] = pickupTime.split(":");
          const pickUpDateObj = new Date(pickupDate);
          pickUpDateObj.setHours(
            parseInt(pickupHours),
            parseInt(pickupMinutes)
          );

          let returnDateObj = null;
          let returnHours = null;
          let returnMinutes = null;

          if (serviceType === "round-trip" && returnDate && returnTime) {
            [returnHours, returnMinutes] = returnTime.split(":");
            returnDateObj = new Date(returnDate);
            returnDateObj.setHours(
              parseInt(returnHours),
              parseInt(returnMinutes)
            );

            if (returnDateObj < pickUpDateObj) {
              Swal.showValidationMessage(
                "Return date cannot be before pickup date"
              );
              return false;
            }
          }

          // Calculate season based on date
          const season = getSeason(pickUpDateObj);

          // Calculate price if standard locations are selected
          let price = null;
          if (pickupLocation !== "other" && dropoffLocation !== "other") {
            price = calculateTransferPrice(
              pickupLocation,
              dropoffLocation,
              season,
              carType
            );
          }

          // Create final form data
          return {
            name,
            surname,
            email,
            phone,
            carType,
            persons,
            luggage,
            serviceType,
            pickupLocation:
              pickupLocation === "other" ? otherPickupLocation : pickupLocation,
            dropoffLocation:
              dropoffLocation === "other"
                ? otherDropoffLocation
                : dropoffLocation,
            pickUpDate: pickUpDateObj,
            pickupHours,
            pickupMinutes,
            dropOffDate: pickUpDateObj, // For one-way, drop-off date is same as pickup
            dropoffHours: pickupHours,
            dropoffMinutes: pickupMinutes,
            returnDate: returnDateObj,
            returnHours,
            returnMinutes,
            paymentMethod,
            airportTicketId,
            atDisposal,
            disposalHours: atDisposal ? disposalHours : null,
            helicopterTransfer,
            additionalInfo,
            status: "pending",
            isReturn: serviceType === "round-trip",
            seasonType: season,
            price: price,
            createdByPartner: partnerData.partnerId,
            partnerCommission: partnerData.commission,
          };
        },
      });

      if (transferDismiss) {
        setCreatingPartnerTransfer(false);
        return;
      } // Make sure partner data is correctly set in the final transfer data
      const transferData = {
        ...formValues,
        // Ensure these fields are explicitly set to make sure they're not lost
        partnerCommission: partnerData.commission,
        partnerId: partnerData.partnerId,
        createdByPartner: partnerData.partnerId,
      };

      // Create the transfer
      await contact.submitTransferRequest(transferData);

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Partner transfer created successfully",
        confirmButtonColor: "#1e3a8a",
      });

      fetchData();
    } catch (error) {
      console.error("Error creating partner transfer:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to create partner transfer. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setCreatingPartnerTransfer(false);
    }
  };

  // Driver management functions
  const handleDriverCreate = async () => {
    try {
      setCreatingDriver(true);

      const { value: formValues, dismiss } = await Swal.fire({
        title: "Create New Driver",
        html: `
          <div class="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label for="name" class="block text-sm font-medium text-left mb-1">First Name</label>
              <input id="name" type="text" class="w-full px-3 py-2 border rounded-md" required>
            </div>
            <div>
              <label for="surname" class="block text-sm font-medium text-left mb-1">Last Name</label>
              <input id="surname" type="text" class="w-full px-3 py-2 border rounded-md" required>
            </div>
          </div>
          <div class="mb-4">
            <label for="email" class="block text-sm font-medium text-left mb-1">Email</label>
            <input id="email" type="email" class="w-full px-3 py-2 border rounded-md" required>
          </div>
          <div class="mb-4">
            <label for="password" class="block text-sm font-medium text-left mb-1">Password</label>
            <input id="password" type="password" class="w-full px-3 py-2 border rounded-md" required minlength="6">
            <p class="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
          </div>
          <div class="mb-4">
            <label for="phone" class="block text-sm font-medium text-left mb-1">Phone Number</label>
            <input id="phone" type="text" class="w-full px-3 py-2 border rounded-md" required>
          </div>
          <div class="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label for="licenseNumber" class="block text-sm font-medium text-left mb-1">License Number</label>
              <input id="licenseNumber" type="text" class="w-full px-3 py-2 border rounded-md" required>
            </div>
            <div>
              <label for="licenseExpiry" class="block text-sm font-medium text-left mb-1">License Expiry Date</label>
              <input id="licenseExpiry" type="date" class="w-full px-3 py-2 border rounded-md" required>
            </div>
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium text-left mb-1">Vehicle Types</label>
            <div class="grid grid-cols-2 gap-2">
              <div class="flex items-center">
                <input id="car" type="checkbox" class="mr-2" value="car" checked>
                <label for="car">Car</label>
              </div>
              <div class="flex items-center">
                <input id="minivan" type="checkbox" class="mr-2" value="minivan">
                <label for="minivan">Mini Van</label>
              </div>
              <div class="flex items-center">
                <input id="van" type="checkbox" class="mr-2" value="van">
                <label for="van">Van</label>
              </div>
              <div class="flex items-center">
                <input id="minibus" type="checkbox" class="mr-2" value="minibus">
                <label for="minibus">Mini Bus</label>
              </div>
            </div>
          </div>
          <div class="mb-4">
            <label for="notes" class="block text-sm font-medium text-left mb-1">Notes</label>
            <textarea id="notes" class="w-full px-3 py-2 border rounded-md"></textarea>
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: "Create",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#1e3a8a",
        preConfirm: () => {
          const vehicleTypes = [];
          if (document.getElementById("car").checked) vehicleTypes.push("car");
          if (document.getElementById("minivan").checked)
            vehicleTypes.push("minivan");
          if (document.getElementById("van").checked) vehicleTypes.push("van");
          if (document.getElementById("minibus").checked)
            vehicleTypes.push("minibus");

          if (vehicleTypes.length === 0) {
            Swal.showValidationMessage(
              "Please select at least one vehicle type"
            );
            return false;
          }

          return {
            name: document.getElementById("name").value,
            surname: document.getElementById("surname").value,
            email: document.getElementById("email").value,
            password: document.getElementById("password").value,
            phone: document.getElementById("phone").value,
            licenseNumber: document.getElementById("licenseNumber").value,
            licenseExpiry: document.getElementById("licenseExpiry").value,
            vehicleTypes,
            notes: document.getElementById("notes").value,
          };
        },
      });

      if (dismiss) return;

      // Create driver
      await drivers.createDriver(formValues);

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Driver created successfully",
        confirmButtonColor: "#1e3a8a",
      });

      fetchData();
    } catch (error) {
      console.error("Error creating driver:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to create driver. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setCreatingDriver(false);
    }
  };

  const handleDriverEdit = async (driverId) => {
    try {
      setEditingDriver(driverId);

      // Find driver data
      const driver = driversData.find((d) => d._id === driverId);
      if (!driver) {
        throw new Error("Driver not found");
      }

      const { value: formValues, dismiss } = await Swal.fire({
        title: "Edit Driver",
        html: `
          <div class="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label for="name" class="block text-sm font-medium text-left mb-1">First Name</label>
              <input id="name" type="text" class="w-full px-3 py-2 border rounded-md" value="${
                driver.name
              }" required>
            </div>
            <div>
              <label for="surname" class="block text-sm font-medium text-left mb-1">Last Name</label>
              <input id="surname" type="text" class="w-full px-3 py-2 border rounded-md" value="${
                driver.surname
              }" required>
            </div>
          </div>
          <div class="mb-4">
            <label for="email" class="block text-sm font-medium text-left mb-1">Email</label>
            <input id="email" type="email" class="w-full px-3 py-2 border rounded-md" value="${
              driver.email
            }" required>
          </div>
          <div class="mb-4">
            <label for="phone" class="block text-sm font-medium text-left mb-1">Phone Number</label>
            <input id="phone" type="text" class="w-full px-3 py-2 border rounded-md" value="${
              driver.phone
            }" required>
          </div>
          <div class="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label for="licenseNumber" class="block text-sm font-medium text-left mb-1">License Number</label>
              <input id="licenseNumber" type="text" class="w-full px-3 py-2 border rounded-md" value="${
                driver.licenseNumber
              }" required>
            </div>
            <div>
              <label for="licenseExpiry" class="block text-sm font-medium text-left mb-1">License Expiry Date</label>
              <input id="licenseExpiry" type="date" class="w-full px-3 py-2 border rounded-md" value="${
                new Date(driver.licenseExpiry).toISOString().split("T")[0]
              }" required>
            </div>
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium text-left mb-1">Vehicle Types</label>
            <div class="grid grid-cols-2 gap-2">
              <div class="flex items-center">
                <input id="car" type="checkbox" class="mr-2" value="car" ${
                  driver.vehicleTypes.includes("car") ? "checked" : ""
                }>
                <label for="car">Car</label>
              </div>
              <div class="flex items-center">
                <input id="minivan" type="checkbox" class="mr-2" value="minivan" ${
                  driver.vehicleTypes.includes("minivan") ? "checked" : ""
                }>
                <label for="minivan">Mini Van</label>
              </div>
              <div class="flex items-center">
                <input id="van" type="checkbox" class="mr-2" value="van" ${
                  driver.vehicleTypes.includes("van") ? "checked" : ""
                }>
                <label for="van">Van</label>
              </div>
              <div class="flex items-center">
                <input id="minibus" type="checkbox" class="mr-2" value="minibus" ${
                  driver.vehicleTypes.includes("minibus") ? "checked" : ""
                }>
                <label for="minibus">Mini Bus</label>
              </div>
            </div>
          </div>
          <div class="mb-4">
            <label for="isActive" class="flex items-center">
              <input id="isActive" type="checkbox" class="mr-2" ${
                driver.isActive ? "checked" : ""
              }>
              <span class="text-sm font-medium">Active</span>
            </label>
          </div>
          <div class="mb-4">
            <label for="notes" class="block text-sm font-medium text-left mb-1">Notes</label>
            <textarea id="notes" class="w-full px-3 py-2 border rounded-md">${
              driver.notes || ""
            }</textarea>
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: "Update",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#1e3a8a",
        preConfirm: () => {
          const vehicleTypes = [];
          if (document.getElementById("car").checked) vehicleTypes.push("car");
          if (document.getElementById("minivan").checked)
            vehicleTypes.push("minivan");
          if (document.getElementById("van").checked) vehicleTypes.push("van");
          if (document.getElementById("minibus").checked)
            vehicleTypes.push("minibus");

          if (vehicleTypes.length === 0) {
            Swal.showValidationMessage(
              "Please select at least one vehicle type"
            );
            return false;
          }

          return {
            name: document.getElementById("name").value,
            surname: document.getElementById("surname").value,
            email: document.getElementById("email").value,
            phone: document.getElementById("phone").value,
            licenseNumber: document.getElementById("licenseNumber").value,
            licenseExpiry: document.getElementById("licenseExpiry").value,
            vehicleTypes,
            isActive: document.getElementById("isActive").checked,
            notes: document.getElementById("notes").value,
          };
        },
      });

      if (dismiss) return;

      // Update driver
      await drivers.updateDriver(driverId, formValues);

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Driver updated successfully",
        confirmButtonColor: "#1e3a8a",
      });

      fetchData();
    } catch (error) {
      console.error("Error updating driver:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update driver. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setEditingDriver(null);
    }
  };

  const handleDriverDelete = async (driverId) => {
    try {
      // Check if driver has transfers
      const hasTransfers = transfersData.some(
        (transfer) =>
          transfer.assignedDriver && transfer.assignedDriver._id === driverId
      );

      if (hasTransfers) {
        await Swal.fire({
          icon: "warning",
          title: "Warning",
          text: "This driver has assigned transfers. Please reassign them first.",
          confirmButtonColor: "#1e3a8a",
        });
        return;
      }

      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to recover this driver!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#1e3a8a",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        setDeletingDriverId(driverId);
        await drivers.deleteDriver(driverId);
        await Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "The driver has been deleted.",
          confirmButtonColor: "#1e3a8a",
        });
        fetchData();
      }
    } catch (error) {
      console.error("Error deleting driver:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete driver. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setDeletingDriverId(null);
    }
  };

  const handleDriverPasswordChange = async (driverId) => {
    try {
      setChangingDriverPassword(driverId);

      const { value: password, dismiss } = await Swal.fire({
        title: "Change Password",
        html: `
          <div class="mb-4">
            <label for="password" class="block text-sm font-medium text-left mb-1">New Password</label>
            <input id="password" type="password" class="w-full px-3 py-2 border rounded-md" required minlength="6">
            <p class="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
          </div>
          <div class="mb-4">
            <label for="confirmPassword" class="block text-sm font-medium text-left mb-1">Confirm Password</label>
            <input id="confirmPassword" type="password" class="w-full px-3 py-2 border rounded-md" required>
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: "Update Password",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#1e3a8a",
        preConfirm: () => {
          const password = document.getElementById("password").value;
          const confirmPassword =
            document.getElementById("confirmPassword").value;

          if (password.length < 6) {
            Swal.showValidationMessage(
              "Password must be at least 6 characters"
            );
            return false;
          }

          if (password !== confirmPassword) {
            Swal.showValidationMessage("Passwords do not match");
            return false;
          }

          return password;
        },
      });

      if (dismiss) return;

      // Update password
      await drivers.updateDriverPassword(driverId, password);

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Password updated successfully",
        confirmButtonColor: "#1e3a8a",
      });
    } catch (error) {
      console.error("Error updating password:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update password. Please try again.",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setChangingDriverPassword(null);
    }
  };

   const handleCreateTour = () => {
    MySwal.fire({
      title: "Create New Tour",
      html: <CreateTourForm ref={formRef} />,
      showCancelButton: true,
      confirmButtonText: "Create Tour",
      confirmButtonColor: "#1e3a8a",
      cancelButtonColor: "#d33",
      width: '800px', 
      preConfirm: () => {
        const tourData = formRef.current?.getFormData();

        if (!tourData) {
           Swal.showValidationMessage(
            `Please fill all required fields correctly.` 
           );
           return false; 
         }
        return tourData;
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const tourData = result.value;

        // Create FormData object
        const formData = new FormData();

        // Append all tour data
        formData.append("name", tourData.name);
        formData.append("description", tourData.description);
        formData.append("duration", tourData.duration);
        formData.append("maxPersons", tourData.maxPersons);
        formData.append("vehicleType", tourData.vehicleType);
        formData.append("isActive", tourData.isActive);

        // Add the image file if it exists
        if (tourData.tourImage) {
          formData.append("tourImage", tourData.tourImage);
        }

        for (const vehicle in tourData.pricing) {
           for (const season in tourData.pricing[vehicle]) {
               formData.append(`pricing[${vehicle}][${season}]`, tourData.pricing[vehicle][season]);
           }
        }

        // Append locations and highlights arrays
        tourData.locations.forEach(loc => formData.append('locations[]', loc));
        tourData.highlights.forEach(h => formData.append('highlights[]', h));

        tours
          .create(formData)
          .then(() => {
            MySwal.fire({
              icon: "success",
              title: "Tour Created!",
              text: "The tour has been created successfully.",
              confirmButtonColor: "#1e3a8a",
              iconColor: "#10B981", // Use MySwal's iconColor prop
            });
            fetchData(); // Refresh tour data
          })
          .catch((error) => {
            console.error("Error creating tour:", error);
            // Attempt to get specific error message from backend response if available
            const errorMsg = error.response?.data?.message || error.message || "An unknown error occurred.";
            MySwal.fire({ // Use MySwal
              icon: "error",
              title: "Creation Failed",
              text: `Failed to create tour: ${errorMsg}`,
              confirmButtonColor: "#1e3a8a",
            });
          });
      }
    });
  };

  const handleEditTour = (tourId) => {
    const tour = toursData.find((t) => t._id === tourId); 
    if (!tour) {
      console.error("Tour not found:", tourId);
      return;
    }
    MySwal.fire({
      title: "Edit Tour",
      html: <EditTourForm ref={formRef} tour={tour} />,
      showCancelButton: true,
      confirmButtonText: "Update Tour",
      confirmButtonColor: "#1e3a8a",
      cancelButtonColor: "#d33",
      width: '800px',
      preConfirm: () => {
        const tourData = formRef.current?.getFormData();
        if (!tourData) {
          Swal.showValidationMessage(
            `Please fill all required fields correctly.`
          );
          return false;
        }
        return tourData;
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const tourData = result.value;
        const formData = new FormData();
        formData.append("name", tourData.name);
        formData.append("description", tourData.description);
        formData.append("duration", tourData.duration);
        formData.append("maxPersons", tourData.maxPersons);
        formData.append("vehicleType", tourData.vehicleType);
        formData.append("isActive", tourData.isActive);
        if (tourData.tourImage) {
          formData.append("tourImage", tourData.tourImage);
        }
        for (const vehicle in tourData.pricing) {
          for (const season in tourData.pricing[vehicle]) {
            formData.append(
              `pricing[${vehicle}][${season}]`,
              tourData.pricing[vehicle][season]
            );
          }
        }

        tourData.locations.forEach((loc) =>
          formData.append("locations[]", loc)
        );
        tourData.highlights.forEach((h) =>
          formData.append("highlights[]", h)
        );
        tours
          .update(tourId, formData)

          .then(() => {
            MySwal.fire({
              icon: "success",
              title: "Tour Updated!",
              text: "The tour has been updated successfully.",
              confirmButtonColor: "#1e3a8a",
              iconColor: "#10B981",
            });
            fetchData(); // Refresh tour data

          })
          .catch((error) => {
            console.error("Error updating tour:", error);
            const errorMsg =
              error.response?.data?.message || error.message || "An unknown error occurred.";
            MySwal.fire({
              icon: "error",
              title: "Update Failed",
              text: `Failed to update tour: ${errorMsg}`,
              confirmButtonColor: "#1e3a8a",
            });
          });
      }
    });
  };


  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <LoadingSpinner />
        </div>
        <Footer />
      </div>
    );
  }
  const tabs = [
    ...(window.location.hostname === "localhost" ||
    window.location.hostname === "transfer-athens.online"
      ? []
      : [
          { id: "cars", name: "Cars", icon: FaCar },
          { id: "rentals", name: "Rentals", icon: FaTicketAlt },
        ]),
    { id: "transfers", name: "Transfers", icon: FaExchangeAlt },
    { id: "transferPricing", name: "Transfer Pricing", icon: FaTags },
    { id: "tours", name: "Tours", icon: FaGlobe },
    { id: "drivers", name: "Drivers", icon: FaUserTie },
    { id: "finances", name: "Finances", icon: FaMoneyBill },
    ...(window.location.hostname === "localhost" ||
    window.location.hostname === "transfer-athens.online"
      ? []
      : [{ id: "coupons", name: "Coupons", icon: FaUsers }]),
    { id: "partners", name: "Partners", icon: FaHandshake },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-t from-gray-300 via-white to-slate-800">
      <Header />
      <div className="flex-grow container mx-auto px-4 py-12">
        <div className=" rounded-lg shadow-md p-6 bg-gradient-to-b from-gray-300 to-gray-600">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              Admin Dashboard
            </h1>
            <NotificationSystem
              rentals={rentalsData}
              onRentalUpdate={handleRentalStatusUpdate}
            />
          </div>

          {/* Tabs */}
          <div className="overflow-x-auto pb-1 -mb-px">
            <div className="flex space-x-1 sm:space-x-4 border-b border-gray-200 mb-6 min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center whitespace-nowrap px-3 py-2 border-b-2 font-medium text-xs sm:text-sm ${
                    activeTab === tab.id
                      ? "border-blue-900 text-blue-900"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <tab.icon className="mr-1 sm:mr-2 h-4 w-4" />
                  <span className="hidden xs:inline">{tab.name}</span>
                  <span className="xs:hidden">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Filters for Cars Tab */}
          {activeTab === "cars" && (
            <>
              {/* Cars Statistics Dashboard */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-br from-blue-500 to-blue-700 p-6 rounded-xl shadow-lg"
                >
                  <div className="flex items-center justify-between">
                    <div className="p-3 rounded-full bg-blue-400 bg-opacity-40">
                      <FaCar className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-white text-sm opacity-80">
                        Total Cars
                      </p>
                      <p className="text-2xl font-bold text-white">
                        {carsData.length}
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-gradient-to-br from-green-500 to-green-700 p-6 rounded-xl shadow-lg"
                >
                  <div className="flex items-center justify-between">
                    <div className="p-3 rounded-full bg-green-400 bg-opacity-40">
                      <FaCheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-white text-sm opacity-80">Available</p>
                      <p className="text-2xl font-bold text-white">
                        {
                          carsData.filter((car) => car.status === "available")
                            .length
                        }
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-gradient-to-br from-yellow-500 to-yellow-700 p-6 rounded-xl shadow-lg"
                >
                  <div className="flex items-center justify-between">
                    <div className="p-3 rounded-full bg-yellow-400 bg-opacity-40">
                      <FaTools className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-white text-sm opacity-80">
                        In Maintenance
                      </p>
                      <p className="text-2xl font-bold text-white">
                        {
                          carsData.filter((car) => car.status === "maintenance")
                            .length
                        }
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-gradient-to-br from-red-500 to-red-700 p-6 rounded-xl shadow-lg"
                >
                  <div className="flex items-center justify-between">
                    <div className="p-3 rounded-full bg-red-400 bg-opacity-40">
                      <FaBan className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-white text-sm opacity-80">
                        Unavailable
                      </p>
                      <p className="text-2xl font-bold text-white">
                        {
                          carsData.filter((car) => car.status === "unavailable")
                            .length
                        }
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Search and Filter Section */}
              <div className="mb-6">
                <div className="flex flex-wrap gap-4 mb-4">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      placeholder="Search cars..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 pl-12 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300"
                    />
                    <FaSearch className="absolute left-4 top-3.5 text-gray-400" />
                  </div>
                  <motion.button
                    onClick={() => setCreateCarModalOpen(true)}
                    disabled={isCreatingCar}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg hover:from-blue-700 hover:to-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg"
                  >
                    {isCreatingCar ? (
                      <div className="flex items-center">
                        <LoadingSpinner size="sm" />
                        <span className="ml-2">Creating...</span>
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <FaPlus className="mr-2" />
                        <span>Add New Car</span>
                      </div>
                    )}
                  </motion.button>
                </div>

                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-200">
                    <FaFilter className="text-gray-500" />
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="border-none focus:ring-0 text-sm text-gray-600"
                    >
                      <option value="all">All Categories</option>
                      {categories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-200">
                    <FaCar className="text-gray-500" />
                    <select
                      value={selectedTransmission}
                      onChange={(e) => setSelectedTransmission(e.target.value)}
                      className="border-none focus:ring-0 text-sm text-gray-600"
                    >
                      <option value="all">All Transmissions</option>
                      <option value="manual">Manual</option>
                      <option value="automatic">Automatic</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Car Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filterCars(carsData).map((car) => (
                  <motion.div
                    key={car._id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    whileHover={{ y: -5 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300"
                  >
                    <div className="relative aspect-w-16 aspect-h-9 bg-gray-100">
                      <img
                        src={getImagePath(car.images[0])}
                        alt={car.make + " " + car.model}
                        className="object-contain w-full h-[200px] p-4"
                      />
                      <div className="absolute top-4 right-4 space-x-2">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            car.status === "available"
                              ? "bg-green-100 text-green-800"
                              : car.status === "maintenance"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {car.status?.charAt(0).toUpperCase() +
                            car.status?.slice(1) || "Available"}
                        </span>
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-1">
                            {car.make} {car.model}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {car.year} · {car.category}
                          </p>
                        </div>
                        <button
                          onClick={() => handleEditCar(car)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                        >
                          <FaEdit className="w-5 h-5" />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="flex items-center text-gray-600">
                          <FaUser className="w-4 h-4 mr-2" />
                          <span>{car.seats} Seats</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <FaCog className="w-4 h-4 mr-2" />
                          <span>{car.transmission}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <FaSuitcase className="w-4 h-4 mr-2" />
                          <span>{car.suitcases} Suitcases</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <FaGasPump className="w-4 h-4 mr-2" />
                          <span>{car.fuelType}</span>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="text-sm text-gray-500">
                              Base Price
                            </span>
                            <p className="text-lg font-bold text-blue-600">
                              €{car.basePrice}/day
                            </p>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">Units</span>
                            <p className="text-lg font-bold text-gray-900">
                              {car.totalUnits || 0}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Create Car Modal */}
              <CreateCarModal
                isOpen={createCarModalOpen}
                onClose={() => setCreateCarModalOpen(false)}
                onSave={handleCreateCar}
                isCreating={isCreatingCar}
              />

              {/* Car Details Modal */}
              {selectedCar && (
                <CarDetailsModal
                  car={selectedCar}
                  isOpen={carDetailsModalOpen}
                  onClose={() => {
                    setCarDetailsModalOpen(false);
                    setSelectedCar(null);
                  }}
                  onSave={(data) => handleCarUpdate(selectedCar._id, data)}
                  onDelete={handleCarDelete}
                  onSeasonSelect={handleSeasonSelect}
                  isUpdating={updatingCarId === selectedCar._id}
                  isDeleting={deletingCarId === selectedCar._id}
                  settingSeasonalPrice={settingSeasonalPrice}
                  categories={categories}
                  editingSeasonalPrice={editingSeasonalPrice}
                  onEditingSeasonalPriceChange={setEditingSeasonalPrice}
                  seasonalPricing={seasonalPricing}
                />
              )}
            </>
          )}

          {activeTab === "rentals" && (
            <div className="space-y-4">
              {/* Daily Turnover Summary */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-gray-900">
                    Today's Rental Turnover -{" "}
                    {moment().format("dddd, MMMM D, YYYY")}
                  </h3>
                  <button
                    onClick={() =>
                      generateDailyPlanPDF(
                        getDailyTurnover().pickups,
                        getDailyTurnover().returns
                      )
                    }
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaDownload className="w-4 h-4" />
                    <span>Download Daily Plan</span>
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Pickups */}
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-green-800 mb-3">
                      Vehicles to Hand Over
                    </h4>
                    <div className="space-y-3">
                      {getDailyTurnover().pickups.length === 0 ? (
                        <p className="text-sm text-gray-600">
                          No vehicle handovers scheduled for today.
                        </p>
                      ) : (
                        getDailyTurnover().pickups.map((rental) => (
                          <div
                            key={rental._id}
                            className="bg-white p-3 rounded-lg border border-green-200"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900">
                                  {rental.carName}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Time:{" "}
                                  {moment(rental.pickupDateTime).format(
                                    "HH:mm"
                                  )}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Location: {rental.pickupLocation}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-gray-600">
                                  {rental.customer.firstName}{" "}
                                  {rental.customer.lastName}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {rental.customer.phoneNumber}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    {/* Pagination for Pickups */}
                    {getDailyTurnover().totalPickups > ITEMS_PER_PAGE && (
                      <div className="flex justify-between items-center mt-4">
                        <button
                          onClick={() =>
                            setTurnoverPage((prev) => ({
                              ...prev,
                              pickups: Math.max(prev.pickups - 1, 1),
                            }))
                          }
                          disabled={turnoverPage.pickups === 1}
                          className="px-3 py-1 rounded-md text-sm font-medium bg-green-100 text-green-800 hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <span className="text-sm text-gray-600">
                          Page {turnoverPage.pickups} of{" "}
                          {Math.ceil(
                            getDailyTurnover().totalPickups / ITEMS_PER_PAGE
                          )}
                        </span>
                        <button
                          onClick={() =>
                            setTurnoverPage((prev) => ({
                              ...prev,
                              pickups: Math.min(
                                prev.pickups + 1,
                                Math.ceil(
                                  getDailyTurnover().totalPickups /
                                    ITEMS_PER_PAGE
                                )
                              ),
                            }))
                          }
                          disabled={
                            turnoverPage.pickups ===
                            Math.ceil(
                              getDailyTurnover().totalPickups / ITEMS_PER_PAGE
                            )
                          }
                          className="px-3 py-1 rounded-md text-sm font-medium bg-green-100 text-green-800 hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Returns */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-blue-800 mb-3">
                      Expected Returns
                    </h4>
                    <div className="space-y-3">
                      {getDailyTurnover().returns.length === 0 ? (
                        <p className="text-sm text-gray-600">
                          No vehicle returns scheduled for today.
                        </p>
                      ) : (
                        getDailyTurnover().returns.map((rental) => (
                          <div
                            key={rental._id}
                            className="bg-white p-3 rounded-lg border border-blue-200"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900">
                                  {rental.carName}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Time:{" "}
                                  {moment(rental.returnDateTime).format(
                                    "HH:mm"
                                  )}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Location: {rental.returnLocation}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-gray-600">
                                  {rental.customer.firstName}{" "}
                                  {rental.customer.lastName}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {rental.customer.phoneNumber}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    {/* Pagination for Returns */}
                    {getDailyTurnover().totalReturns > ITEMS_PER_PAGE && (
                      <div className="flex justify-between items-center mt-4">
                        <button
                          onClick={() =>
                            setTurnoverPage((prev) => ({
                              ...prev,
                              returns: Math.max(prev.returns - 1, 1),
                            }))
                          }
                          disabled={turnoverPage.returns === 1}
                          className="px-3 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <span className="text-sm text-gray-600">
                          Page {turnoverPage.returns} of{" "}
                          {Math.ceil(
                            getDailyTurnover().totalReturns / ITEMS_PER_PAGE
                          )}
                        </span>
                        <button
                          onClick={() =>
                            setTurnoverPage((prev) => ({
                              ...prev,
                              returns: Math.min(
                                prev.returns + 1,
                                Math.ceil(
                                  getDailyTurnover().totalReturns /
                                    ITEMS_PER_PAGE
                                )
                              ),
                            }))
                          }
                          disabled={
                            turnoverPage.returns ===
                            Math.ceil(
                              getDailyTurnover().totalReturns / ITEMS_PER_PAGE
                            )
                          }
                          className="px-3 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Tomorrow Turnover Summary */}
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-gray-900">
                    Tomorrow's Rental Turnover -{" "}
                    {moment().add(1, "days").format("dddd, MMMM D, YYYY")}
                  </h3>
                  <button
                    onClick={() =>
                      generateDailyPlanPDF(
                        getTomorrowTurnover().pickups,
                        getTomorrowTurnover().returns,
                        (isTomorrow = true)
                      )
                    }
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaDownload className="w-4 h-4" />
                    <span>Download Tomorrow's Plan</span>
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Tomorrow's Pickups */}
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-green-800 mb-3">
                      Vehicles to Hand Over
                    </h4>
                    <div className="space-y-3">
                      {getTomorrowTurnover().pickups.length === 0 ? (
                        <p className="text-sm text-gray-600">
                          No vehicle handovers scheduled for tomorrow.
                        </p>
                      ) : (
                        getTomorrowTurnover().pickups.map((rental) => (
                          <div
                            key={rental._id}
                            className="bg-white p-3 rounded-lg border border-green-200"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900">
                                  {rental.carName}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Time:{" "}
                                  {moment(rental.pickupDateTime).format(
                                    "HH:mm"
                                  )}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Location: {rental.pickupLocation}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-gray-600">
                                  {rental.customer.firstName}{" "}
                                  {rental.customer.lastName}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {rental.customer.phoneNumber}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    {/* Pagination for Tomorrow's Pickups */}
                    {getTomorrowTurnover().totalPickups > ITEMS_PER_PAGE && (
                      <div className="flex justify-between items-center mt-4">
                        <button
                          onClick={() =>
                            setTurnoverPage((prev) => ({
                              ...prev,
                              pickups: Math.max(prev.pickups - 1, 1),
                            }))
                          }
                          disabled={turnoverPage.pickups === 1}
                          className="px-3 py-1 rounded-md text-sm font-medium bg-green-100 text-green-800 hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <span className="text-sm text-gray-600">
                          Page {turnoverPage.pickups} of{" "}
                          {Math.ceil(
                            getTomorrowTurnover().totalPickups / ITEMS_PER_PAGE
                          )}
                        </span>
                        <button
                          onClick={() =>
                            setTurnoverPage((prev) => ({
                              ...prev,
                              pickups: Math.min(
                                prev.pickups + 1,
                                Math.ceil(
                                  getTomorrowTurnover().totalPickups /
                                    ITEMS_PER_PAGE
                                )
                              ),
                            }))
                          }
                          disabled={
                            turnoverPage.pickups ===
                            Math.ceil(
                              getTomorrowTurnover().totalPickups /
                                ITEMS_PER_PAGE
                            )
                          }
                          className="px-3 py-1 rounded-md text-sm font-medium bg-green-100 text-green-800 hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Tomorrow's Returns */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-blue-800 mb-3">
                      Expected Returns
                    </h4>
                    <div className="space-y-3">
                      {getTomorrowTurnover().returns.length === 0 ? (
                        <p className="text-sm text-gray-600">
                          No vehicle returns scheduled for tomorrow.
                        </p>
                      ) : (
                        getTomorrowTurnover().returns.map((rental) => (
                          <div
                            key={rental._id}
                            className="bg-white p-3 rounded-lg border border-blue-200"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900">
                                  {rental.carName}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Time:{" "}
                                  {moment(rental.returnDateTime).format(
                                    "HH:mm"
                                  )}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Location: {rental.returnLocation}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-gray-600">
                                  {rental.customer.firstName}{" "}
                                  {rental.customer.lastName}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {rental.customer.phoneNumber}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    {/* Pagination for Tomorrow's Returns */}
                    {getTomorrowTurnover().totalReturns > ITEMS_PER_PAGE && (
                      <div className="flex justify-between items-center mt-4">
                        <button
                          onClick={() =>
                            setTurnoverPage((prev) => ({
                              ...prev,
                              returns: Math.max(prev.returns - 1, 1),
                            }))
                          }
                          disabled={turnoverPage.returns === 1}
                          className="px-3 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <span className="text-sm text-gray-600">
                          Page {turnoverPage.returns} of{" "}
                          {Math.ceil(
                            getTomorrowTurnover().totalReturns / ITEMS_PER_PAGE
                          )}
                        </span>
                        <button
                          onClick={() =>
                            setTurnoverPage((prev) => ({
                              ...prev,
                              returns: Math.min(
                                prev.returns + 1,
                                Math.ceil(
                                  getTomorrowTurnover().totalReturns /
                                    ITEMS_PER_PAGE
                                )
                              ),
                            }))
                          }
                          disabled={
                            turnoverPage.returns ===
                            Math.ceil(
                              getTomorrowTurnover().totalReturns /
                                ITEMS_PER_PAGE
                            )
                          }
                          className="px-3 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Search and Filter Section */}
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search rentals..."
                      value={rentalSearch}
                      onChange={(e) => setRentalSearch(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 pl-10"
                    />
                    <FaSearch className="absolute left-3 top-3 text-gray-400" />
                  </div>

                  <div>
                    <select
                      value={rentalStatusFilter}
                      onChange={(e) => setRentalStatusFilter(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    >
                      <option value="all">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  <DatePicker
                    selected={dateFilter.startDate}
                    onChange={(date) =>
                      setDateFilter((prev) => ({ ...prev, startDate: date }))
                    }
                    placeholderText="Start Date"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    dateFormat="dd/MM/yyyy"
                  />

                  <DatePicker
                    selected={dateFilter.endDate}
                    onChange={(date) =>
                      setDateFilter((prev) => ({ ...prev, endDate: date }))
                    }
                    placeholderText="End Date"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    dateFormat="dd/MM/yyyy"
                    minDate={dateFilter.startDate}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6">
                {filteredRentals.map((rental) => (
                  <motion.div
                    key={rental._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200"
                  >
                    <div className="p-6">
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        {/* Customer Info */}
                        <div className="flex items-start space-x-4">
                          <div className="bg-blue-100 rounded-full p-3">
                            <FaUser className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {rental.customer.firstName}{" "}
                              {rental.customer.lastName}
                            </h3>
                            <p className="text-sm text-gray-500">
                              {rental.customer.email}
                            </p>
                            <p className="text-sm text-gray-500">
                              Phone: {rental.customer.phoneNumber} | Age:{" "}
                              {rental.customer.age}
                            </p>
                          </div>
                        </div>

                        {/* Status and Actions */}
                        <div className="flex flex-wrap items-center gap-3">
                          <select
                            value={rental.status}
                            onChange={(e) =>
                              handleRentalStatusUpdate(
                                rental._id,
                                e.target.value
                              )
                            }
                            disabled={updatingRentalId === rental._id}
                            className={`relative inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                              rental.status === "completed"
                                ? "bg-green-100 text-green-800"
                                : rental.status === "cancelled"
                                ? "bg-red-100 text-red-800"
                                : rental.status === "confirmed"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            <option value="pending">⏳ Pending</option>
                            <option value="confirmed">✓ Confirmed</option>
                            <option value="completed">★ Completed</option>
                            <option value="cancelled">✖ Cancelled</option>
                          </select>

                          <button
                            onClick={() =>
                              handlePaymentStatusUpdate(
                                rental._id,
                                !rental.isPaid
                              )
                            }
                            disabled={updatingPaymentId === rental._id}
                            className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
                              rental.isPaid
                                ? "bg-green-100 text-green-800 hover:bg-green-200"
                                : "bg-red-100 text-red-800 hover:bg-red-200"
                            }`}
                          >
                            {rental.isPaid ? "✓ Paid" : "✕ Unpaid"}
                          </button>

                          <button
                            onClick={() => handleRentalDelete(rental._id)}
                            disabled={deletingRentalId === rental._id}
                            className="inline-flex items-center px-3 py-2 border border-red-200 text-red-600 rounded-md hover:bg-red-50"
                          >
                            <FaTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Rental Details */}
                      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Vehicle Details */}
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-gray-700">
                            Vehicle Details
                          </h4>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-base font-medium text-gray-900">
                              {rental.carName}
                            </p>
                            <div className="mt-2 space-y-1 text-sm text-gray-600">
                              <p>Category: {rental.car?.category}</p>
                              <p>Transmission: {rental.car?.transmission}</p>
                              <p>
                                Seats:{" "}
                                {rental.car?.seats || rental.car?.persons}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Rental Period */}
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-gray-700">
                            Rental Period
                          </h4>
                          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                            <div className="flex items-center text-sm">
                              <FaCalendarAlt className="w-4 h-4 text-green-500 mr-2" />
                              <div>
                                <p className="font-medium">
                                  Pickup:{" "}
                                  {formatDateTime(rental.pickupDateTime)}
                                </p>
                                <p className="text-gray-500 mt-1">
                                  Location: {rental.pickupLocation}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center text-sm">
                              <FaCalendarAlt className="w-4 h-4 text-red-500 mr-2" />
                              <div>
                                <p className="font-medium">
                                  Return:{" "}
                                  {formatDateTime(rental.returnDateTime)}
                                </p>
                                <p className="text-gray-500 mt-1">
                                  Location: {rental.returnLocation}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Additional Options & Notes */}
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-gray-700">
                            Additional Details
                          </h4>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex flex-wrap gap-2 mb-3">
                              {rental.additionalOptions?.gps && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  <TbGps className="w-4 h-4 mr-1" /> GPS
                                </span>
                              )}
                              {rental.additionalOptions?.childSeat && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  <FaChild className="w-4 h-4 mr-1" /> Child
                                  Seat
                                </span>
                              )}
                              {rental.additionalOptions?.babySeat && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                                  <FaBaby className="w-4 h-4 mr-1" /> Baby Seat
                                </span>
                              )}
                            </div>
                            {rental.additionalNotes && (
                              <div className="mt-3">
                                <p className="text-sm font-medium text-gray-700">
                                  Notes:
                                </p>
                                <p className="text-sm text-gray-600 mt-1">
                                  {rental.additionalNotes}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Price Breakdown */}
                      <div className="mt-6">
                        <button
                          onClick={() =>
                            setExpandedCustomer(
                              expandedCustomer === rental._id
                                ? null
                                : rental._id
                            )
                          }
                          className="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
                        >
                          {expandedCustomer === rental._id ? (
                            <>
                              <FaChevronUp className="mr-1" />
                              Hide Price Details
                            </>
                          ) : (
                            <>
                              <FaChevronDown className="mr-1" />
                              Show Price Details
                            </>
                          )}
                        </button>

                        <AnimatePresence>
                          {expandedCustomer === rental._id && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="mt-4 bg-gray-50 rounded-lg p-4">
                                <h4 className="text-sm font-medium text-gray-700 mb-4">
                                  Price Breakdown
                                </h4>
                                <div className="space-y-2">
                                  {rental.priceBreakdown.map(
                                    (period, index) => (
                                      <div
                                        key={index}
                                        className="flex justify-between text-sm"
                                      >
                                        <span>
                                          {new Date(
                                            period.startDate
                                          ).toLocaleDateString()}{" "}
                                          -{" "}
                                          {new Date(
                                            period.endDate
                                          ).toLocaleDateString()}
                                        </span>
                                        <span>
                                          €{period.pricePerDay} ×{" "}
                                          {period.numberOfDays} days = €
                                          {period.totalForPeriod}
                                        </span>
                                      </div>
                                    )
                                  )}

                                  {rental.discountAmount > 0 && (
                                    <div className="flex justify-between text-sm text-green-600">
                                      <span>
                                        Discount{" "}
                                        {rental.couponCode
                                          ? `(${rental.couponCode})`
                                          : ""}
                                      </span>
                                      <span>-€{rental.discountAmount}</span>
                                    </div>
                                  )}

                                  <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                                    <span>Before VAT:</span>
                                    <span>
                                      €{(rental.totalPrice / 1.24).toFixed(2)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span>VAT (24%):</span>
                                    <span>
                                      €
                                      {(
                                        rental.totalPrice -
                                        rental.totalPrice / 1.24
                                      ).toFixed(2)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-base font-medium pt-2 border-t border-gray-200">
                                    <span>Total Price (inc. VAT):</span>
                                    <span>€{rental.totalPrice}</span>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "transfers" && (
            <>
              <div className="space-y-6">
                {/* Transfers Statistics */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-br from-blue-500 to-blue-700 p-6 rounded-xl shadow-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-full bg-blue-400 bg-opacity-40">
                        <FaExchangeAlt className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <p className="text-white text-sm opacity-80">
                          Total Transfers
                        </p>
                        <p className="text-2xl font-bold text-white">
                          {transfersData.length}
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-gradient-to-br from-yellow-500 to-yellow-700 p-6 rounded-xl shadow-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-full bg-yellow-400 bg-opacity-40">
                        <FaClock className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <p className="text-white text-sm opacity-80">Pending</p>
                        <p className="text-2xl font-bold text-white">
                          {
                            transfersData.filter((t) => t.status === "pending")
                              .length
                          }
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-gradient-to-br from-green-500 to-green-700 p-6 rounded-xl shadow-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-full bg-green-400 bg-opacity-40">
                        <FaCheck className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <p className="text-white text-sm opacity-80">
                          Confirmed
                        </p>
                        <p className="text-2xl font-bold text-white">
                          {
                            transfersData.filter(
                              (t) => t.status === "confirmed"
                            ).length
                          }
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-gradient-to-br from-purple-500 to-purple-700 p-6 rounded-xl shadow-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-full bg-purple-400 bg-opacity-40">
                        <FaUserTie className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <p className="text-white text-sm opacity-80">
                          With Driver
                        </p>
                        <p className="text-2xl font-bold text-white">
                          {transfersData.filter((t) => t.assignedDriver).length}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>{" "}
                {/* Search and Filter Section */}
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 space-y-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                    <h3 className="text-lg font-bold text-gray-900">
                      Transfer Management
                    </h3>
                    <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                      <motion.button
                        onClick={() => handleCreateTransfer()}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex-1 sm:flex-none px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg hover:from-blue-700 hover:to-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 shadow-lg flex items-center justify-center"
                      >
                        <FaPlus className="mr-1 sm:mr-2" />
                        <span className="text-sm sm:text-base">
                          Create Transfer
                        </span>
                      </motion.button>
                      <motion.button
                        onClick={() => handleCreatePartnerTransfer()}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex-1 sm:flex-none px-3 py-2 bg-gradient-to-r from-green-600 to-green-800 text-white rounded-lg hover:from-green-700 hover:to-green-900 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-300 shadow-lg flex items-center justify-center"
                      >
                        <FaHandshake className="mr-1 sm:mr-2" />
                        <span className="text-sm sm:text-base">
                          Partner Transfer
                        </span>
                      </motion.button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search transfers..."
                        value={transferSearch}
                        onChange={(e) => setTransferSearch(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 pl-10"
                      />
                      <FaSearch className="absolute left-3 top-3 text-gray-400" />
                    </div>

                    <select
                      value={transferStatusFilter}
                      onChange={(e) => setTransferStatusFilter(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    >
                      <option value="all">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>

                    <div className="col-span-1 sm:col-span-2 lg:col-span-1">
                      <DatePicker
                        selected={transferDateFilter.startDate}
                        onChange={(date) =>
                          setTransferDateFilter((prev) => ({
                            ...prev,
                            startDate: date,
                          }))
                        }
                        placeholderText="Start Date"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2"
                        dateFormat="dd/MM/yyyy"
                      />
                    </div>

                    <div className="col-span-1 sm:col-span-2 lg:col-span-1">
                      <DatePicker
                        selected={transferDateFilter.endDate}
                        onChange={(date) =>
                          setTransferDateFilter((prev) => ({
                            ...prev,
                            endDate: date,
                          }))
                        }
                        placeholderText="End Date"
                        className="w-full border border-gray-300 rounded-lg px-4 py-2"
                        dateFormat="dd/MM/yyyy"
                        minDate={transferDateFilter.startDate}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-6">
                {transfersData
                  .filter((transfer) => {
                    const matchesSearch =
                      (transfer.name + " " + transfer.surname)
                        .toLowerCase()
                        .includes(transferSearch.toLowerCase()) ||
                      transfer.email
                        .toLowerCase()
                        .includes(transferSearch.toLowerCase()) ||
                      transfer.phone
                        .toLowerCase()
                        .includes(transferSearch.toLowerCase()) ||
                      transfer.pickupLocation
                        .toLowerCase()
                        .includes(transferSearch.toLowerCase()) ||
                      transfer.dropoffLocation
                        .toLowerCase()
                        .includes(transferSearch.toLowerCase());

                    const matchesStatus =
                      transferStatusFilter === "all" ||
                      transfer.status === transferStatusFilter;

                    const matchesDate =
                      !transferDateFilter.startDate ||
                      !transferDateFilter.endDate ||
                      (new Date(transfer.pickUpDate) >=
                        transferDateFilter.startDate &&
                        new Date(transfer.pickUpDate) <=
                          transferDateFilter.endDate);

                    return matchesSearch && matchesStatus && matchesDate;
                  })
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                  .map((transfer) => (
                    <motion.div
                      key={transfer._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200"
                    >
                      <div className="p-6">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                          {/* Customer Info */}
                          <div className="flex items-start space-x-4">
                            <div
                              className={`bg-${
                                transfer.status === "confirmed"
                                  ? "green"
                                  : transfer.status === "completed"
                                  ? "blue"
                                  : transfer.status === "cancelled"
                                  ? "red"
                                  : "yellow"
                              }-100 rounded-full p-3`}
                            >
                              <FaUser
                                className={`w-6 h-6 text-${
                                  transfer.status === "confirmed"
                                    ? "green"
                                    : transfer.status === "completed"
                                    ? "blue"
                                    : transfer.status === "cancelled"
                                    ? "red"
                                    : "yellow"
                                }-600`}
                              />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">
                                {transfer.name} {transfer.surname}
                              </h3>
                              <div className="flex flex-col sm:flex-row sm:space-x-4">
                                <p className="text-sm text-gray-500 flex items-center">
                                  <FaEnvelope className="w-4 h-4 mr-1" />{" "}
                                  {transfer.email}
                                </p>
                                <p className="text-sm text-gray-500 flex items-center">
                                  <FaPhoneAlt className="w-4 h-4 mr-1" />{" "}
                                  {transfer.phone}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Status and Actions */}
                          <div className="flex flex-wrap items-center gap-3">
                            <select
                              value={transfer.status}
                              onChange={(e) =>
                                handleTransferStatusUpdate(
                                  transfer._id,
                                  e.target.value
                                )
                              }
                              disabled={updatingTransferStatus === transfer._id}
                              className={`relative inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                                transfer.status === "completed"
                                  ? "bg-green-100 text-green-800"
                                  : transfer.status === "cancelled"
                                  ? "bg-red-100 text-red-800"
                                  : transfer.status === "confirmed"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              <option value="pending">⏳ Pending</option>
                              <option value="confirmed">✓ Confirmed</option>
                              <option value="completed">★ Completed</option>
                              <option value="cancelled">✖ Cancelled</option>
                            </select>

                            {transfer.assignedDriver ? (
                              <button
                                onClick={() =>
                                  handleUnassignDriver(transfer._id)
                                }
                                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200"
                              >
                                <FaUserTie className="mr-2" /> Change Driver
                              </button>
                            ) : (
                              <button
                                onClick={() =>
                                  handleAssignDriverToTransfer(transfer._id)
                                }
                                disabled={
                                  assigningDriverToTransfer === transfer._id
                                }
                                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-purple-100 text-purple-800 hover:bg-purple-200"
                              >
                                {assigningDriverToTransfer === transfer._id ? (
                                  <span className="flex items-center">
                                    <LoadingSpinner size="sm" />
                                    <span className="ml-2">Assigning...</span>
                                  </span>
                                ) : (
                                  <span className="flex items-center">
                                    <FaUserTie className="mr-2" />
                                    Assign Driver
                                  </span>
                                )}
                              </button>
                            )}

                            <button
                              onClick={() => handleTransferDelete(transfer._id)}
                              disabled={deletingTransferId === transfer._id}
                              className="inline-flex items-center px-3 py-2 border border-red-200 text-red-600 rounded-md hover:bg-red-50"
                            >
                              <FaTrash className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Transfer Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                          {" "}
                          {/* Transfer Type & Vehicle */}
                          <div className="space-y-2">
                            <h4 className="text-sm font-medium text-gray-700">
                              Transfer Details
                            </h4>
                            <div className="bg-gray-50 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-xs font-medium bg-indigo-100 text-indigo-800 px-2.5 py-0.5 rounded-full">
                                  {transfer.serviceType === "one-way"
                                    ? "One Way"
                                    : "Round Trip"}
                                </span>
                                <span className="text-xs font-medium bg-blue-100 text-blue-800 px-2.5 py-0.5 rounded-full">
                                  {transfer.carType.charAt(0).toUpperCase() +
                                    transfer.carType.slice(1)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-500">
                                  Passengers:
                                </span>
                                <span className="font-medium">
                                  {transfer.persons}
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-500">Luggage:</span>
                                <span className="font-medium">
                                  {transfer.luggage}
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-500">Payment:</span>
                                <span className="font-medium capitalize">
                                  {transfer.paymentMethod.replace("-", " ")}
                                </span>
                              </div>
                              {/* Pricing Information */}
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-gray-500">Price:</span>
                                  <span className="font-bold text-green-600">
                                    {transfer.priceTotal || "N/A"}{" "}
                                    {transfer.priceTotal ? "€" : ""}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between text-sm mt-1">
                                  <span className="text-gray-500">Season:</span>
                                  <span className="font-medium capitalize">
                                    {transfer.season
                                      ? `${
                                          transfer.season
                                            .charAt(0)
                                            .toUpperCase() +
                                          transfer.season.slice(1)
                                        } Season`
                                      : "N/A"}
                                  </span>
                                </div>
                                {transfer.partner && (
                                  <div className="flex items-center justify-between text-sm mt-1">
                                    <span className="text-gray-500">
                                      Partner:
                                    </span>
                                    <span className="font-medium">
                                      {typeof transfer.partner === "object"
                                        ? transfer.partner.firstName ||
                                          transfer.partner.companyName
                                        : "Yes"}
                                    </span>
                                  </div>
                                )}
                                {(transfer.partnerCommission ||
                                  transfer.partnerCommission === 0) && (
                                  <div className="flex items-center justify-between text-sm mt-1">
                                    <span className="text-gray-500">
                                      Commission:
                                    </span>
                                    <span className="font-medium text-blue-600">
                                      {transfer.partnerCommission} %
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          {/* Locations */}
                          <div className="space-y-2">
                            <h4 className="text-sm font-medium text-gray-700">
                              Locations
                            </h4>
                            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                              <div className="flex text-sm">
                                <div className="mr-2 flex flex-col items-center">
                                  <div className="w-6 h-6 rounded-full border-2 border-green-500 flex items-center justify-center">
                                    <FaMapMarkerAlt className="text-green-500 w-3 h-3" />
                                  </div>
                                  <div className="w-0.5 h-full bg-gray-300 mt-1"></div>
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">
                                    Pickup
                                  </p>
                                  <p className="text-gray-600">
                                    {transfer.pickupLocation}
                                  </p>
                                </div>
                              </div>
                              <div className="flex text-sm">
                                <div className="mr-2 flex flex-col items-center">
                                  <div className="w-6 h-6 rounded-full border-2 border-red-500 flex items-center justify-center">
                                    <FaMapMarkerAlt className="text-red-500 w-3 h-3" />
                                  </div>
                                  {transfer.serviceType === "round-trip" && (
                                    <div className="w-0.5 h-full bg-gray-300 mt-1"></div>
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">
                                    Drop-off
                                  </p>
                                  <p className="text-gray-600">
                                    {transfer.dropoffLocation}
                                  </p>
                                </div>
                              </div>
                              {transfer.serviceType === "round-trip" &&
                                transfer.returnLocation && (
                                  <div className="flex text-sm">
                                    <div className="mr-2 flex flex-col items-center">
                                      <div className="w-6 h-6 rounded-full border-2 border-purple-500 flex items-center justify-center">
                                        <FaMapMarkerAlt className="text-purple-500 w-3 h-3" />
                                      </div>
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-900">
                                        Return Location
                                      </p>
                                      <p className="text-gray-600">
                                        {transfer.returnLocation}
                                      </p>
                                    </div>
                                  </div>
                                )}
                            </div>
                          </div>{" "}
                          {/* Dates & Times */}
                          <div className="space-y-2">
                            <h4 className="text-sm font-medium text-gray-700">
                              Schedule
                            </h4>
                            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                              <div className="flex items-center text-sm">
                                <FaCalendarAlt className="w-4 h-4 text-blue-500 mr-2" />
                                <div>
                                  <p className="font-medium text-gray-900">
                                    Pickup:{" "}
                                    {moment(transfer.pickUpDate).format(
                                      "DD/MM/YYYY"
                                    )}
                                  </p>
                                  <p className="text-gray-500">
                                    Time: {transfer.pickupHours}:
                                    {transfer.pickupMinutes}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center text-sm">
                                <FaCalendarAlt className="w-4 h-4 text-red-500 mr-2" />
                                <div>
                                  <p className="font-medium text-gray-900">
                                    Drop-off:{" "}
                                    {moment(transfer.dropOffDate).format(
                                      "DD/MM/YYYY"
                                    )}
                                  </p>
                                  <p className="text-gray-500">
                                    Time: {transfer.dropoffHours}:
                                    {transfer.dropoffMinutes}
                                  </p>
                                </div>
                              </div>
                              {transfer.serviceType === "round-trip" &&
                                transfer.returnDate && (
                                  <div className="flex items-center text-sm">
                                    <FaCalendarAlt className="w-4 h-4 text-purple-500 mr-2" />
                                    <div>
                                      <p className="font-medium text-gray-900">
                                        Return:{" "}
                                        {moment(transfer.returnDate).format(
                                          "DD/MM/YYYY"
                                        )}
                                      </p>
                                      <p className="text-gray-500">
                                        Time: {transfer.returnHours}:
                                        {transfer.returnMinutes}
                                      </p>
                                    </div>
                                  </div>
                                )}
                            </div>
                          </div>
                          {/* Driver Assigned */}
                          <div className="space-y-2">
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium text-gray-700">
                                Driver & Partner Assigned
                              </h4>
                              <div className="bg-gray-50 rounded-lg p-4">
                                {(() => {
                                  // Check how assignedDriver is provided (as object or ID string)
                                  if (!transfer.assignedDriver) {
                                    return (
                                      <div className="flex items-center text-sm">
                                        <FaUserTie className="w-4 h-4 text-gray-400 mr-2" />
                                        <p className="font-medium text-gray-500">
                                          No Driver Assigned
                                        </p>
                                      </div>
                                    );
                                  } else if (
                                    typeof transfer.assignedDriver === "object"
                                  ) {
                                    // If driver info is already an object
                                    return (
                                      <div className="flex items-center text-sm">
                                        <FaUserTie className="w-4 h-4 text-green-500 mr-2" />
                                        <div>
                                          <p className="font-medium text-gray-900">
                                            {transfer.assignedDriver.name}{" "}
                                            {transfer.assignedDriver.surname}
                                          </p>
                                          <p className="text-gray-500 text-xs mt-1">
                                            {transfer.assignedDriver.phone}
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  } else {
                                    // If driver is just an ID, find the driver in driversData
                                    const driverId = transfer.assignedDriver;
                                    const driver = driversData.find(
                                      (d) => d._id === driverId
                                    );

                                    if (driver) {
                                      return (
                                        <div className="flex items-center text-sm">
                                          <FaUserTie className="w-4 h-4 text-green-500 mr-2" />
                                          <div>
                                            <p className="font-medium text-gray-900">
                                              {driver.name} {driver.surname}
                                            </p>
                                            <p className="text-gray-500 text-xs mt-1">
                                              {driver.phone}
                                            </p>
                                          </div>
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <div className="flex items-center text-sm">
                                          <FaUserTie className="w-4 h-4 text-yellow-500 mr-2" />
                                          <div>
                                            <p className="font-medium text-gray-900">
                                              Driver ID: {driverId}
                                            </p>
                                            <p className="text-gray-500 text-xs mt-1">
                                              Driver details not available
                                            </p>
                                          </div>
                                        </div>
                                      );
                                    }
                                  }
                                })()}
                                {(() => {
                                  // Check if partner info is available as an object
                                  if (transfer.partner) {
                                    return (
                                      <div className="flex items-center text-sm">
                                        <FaUserTie className="w-4 h-4 text-purple-500 mr-2" />
                                        <div>
                                          <p className="font-medium text-gray-900">
                                            {transfer.partner.firstName ||
                                              transfer.partner.companyName}
                                          </p>
                                          <p className="text-gray-500 text-xs mt-1">
                                            {transfer.partner.email}
                                          </p>
                                          <p className="text-gray-500 text-xs mt-1">
                                            {transfer.partner.phone}
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  }
                                  // Check if createdByPartner ID exists
                                  else if (transfer.createdByPartner) {
                                    // Find the partner in partnersData using the ID
                                    const partner = partnersData.find(
                                      (p) => p._id === transfer.createdByPartner
                                    );

                                    if (partner) {
                                      return (
                                        <div className="flex items-center text-sm mt-7">
                                          <FaUserTie className="w-4 h-4 text-purple-500 mr-2" />
                                          <div>
                                            <p className="font-medium text-gray-900">
                                              {partner.firstName ||
                                                partner.companyName}
                                            </p>
                                            <p className="text-gray-500 text-xs mt-1">
                                              {partner.email}
                                            </p>
                                            <p className="text-gray-500 text-xs mt-1">
                                              {partner.phone}
                                            </p>
                                          </div>
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <div className="flex items-center text-sm">
                                          <FaUserTie className="w-4 h-4 text-yellow-500 mr-2" />
                                          <div>
                                            <p className="font-medium text-gray-900">
                                              Partner ID:{" "}
                                              {transfer.createdByPartner}
                                            </p>
                                            <p className="text-gray-500 text-xs mt-1">
                                              Partner details not found
                                            </p>
                                          </div>
                                        </div>
                                      );
                                    }
                                  }
                                  // No partner information available
                                  else {
                                    return (
                                      <div className="flex items-center text-sm">
                                        <FaUserTie className="w-4 h-4 text-gray-400 mr-2" />
                                        <p className="font-medium text-gray-500">
                                          No Partner Available
                                        </p>
                                      </div>
                                    );
                                  }
                                })()}
                              </div>
                            </div>
                            {/* Partner if exists */}
                          </div>
                          {/* Additional Options */}
                        </div>
                      </div>

                      {/* If a tour booking has a transfer, show it here */}
                      {(() => {
                        // Find tour bookings that are linked to this transfer
                        const linkedTourBookings = tourBookingsData.filter(
                          (booking) =>
                            booking.transfer &&
                            booking.transfer._id === transfer._id
                        );

                        if (linkedTourBookings.length > 0) {
                          return (
                            <div className="mt-4 m-6 bg-indigo-50 rounded-lg p-4 border border-indigo-100">
                              <h4 className="text-sm font-medium text-indigo-800 mb-2 flex items-center">
                                <FaGlobe className="mr-2 text-indigo-600" />
                                Connected Tour Booking
                              </h4>
                              {linkedTourBookings.map((booking) => (
                                <div
                                  key={booking._id}
                                  className="flex flex-col space-y-2"
                                >
                                  <div className="flex items-center">
                                    <div className="w-5 h-5 rounded bg-indigo-100 flex items-center justify-center mr-2">
                                      <FaSuitcase className="text-indigo-600 w-3 h-3" />
                                    </div>
                                    <div>
                                      <p className="font-medium">
                                        {booking.tour.name}
                                      </p>
                                      <div className="flex space-x-4">
                                        <p className="text-gray-600 text-sm">
                                          {moment(booking.tourDate).format(
                                            "DD/MM/YYYY"
                                          )}{" "}
                                          at {booking.tourTime.hours}:
                                          {booking.tourTime.minutes}
                                        </p>
                                        <p className="text-gray-600 text-sm">
                                          Persons: {booking.numberOfPersons}
                                        </p>
                                        <p className="text-gray-600 text-sm font-semibold">
                                          €{booking.totalPrice}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="ml-7">
                                    <span
                                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        booking.status === "pending"
                                          ? "bg-yellow-100 text-yellow-800"
                                          : booking.status === "confirmed"
                                          ? "bg-blue-100 text-blue-800"
                                          : booking.status === "completed"
                                          ? "bg-green-100 text-green-800"
                                          : "bg-red-100 text-red-800"
                                      }`}
                                    >
                                      {booking.status.charAt(0).toUpperCase() +
                                        booking.status.slice(1)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* Additional Options */}
                      <div className="m-6 space-y-2">
                        <h4 className="text-sm font-medium text-gray-700">
                          Additional Services
                        </h4>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="space-y-2">
                            {transfer.airportTicketId && (
                              <div className="flex items-center text-sm">
                                <div className="w-5 h-5 rounded bg-blue-100 flex items-center justify-center mr-2">
                                  <FaTruckMoving className="text-blue-600 w-3 h-3" />
                                </div>
                                <div>
                                  <p className="font-medium">
                                    Flight/Ferry Number
                                  </p>
                                  <p className="text-gray-600">
                                    {transfer.airportTicketId}
                                  </p>
                                </div>
                              </div>
                            )}
                            {transfer.atDisposal && (
                              <div className="flex items-center text-sm">
                                <div className="w-5 h-5 rounded bg-purple-100 flex items-center justify-center mr-2">
                                  <FaClock className="text-purple-600 w-3 h-3" />
                                </div>
                                <div>
                                  <p className="font-medium">
                                    Driver at Disposal
                                  </p>
                                  <p className="text-gray-600">
                                    For {transfer.disposalHours} hours
                                  </p>
                                </div>
                              </div>
                            )}
                            {transfer.helicopterTransfer && (
                              <div className="flex items-center text-sm">
                                <div className="w-5 h-5 rounded bg-red-100 flex items-center justify-center mr-2">
                                  <FaTruckMoving className="text-red-600 w-3 h-3" />
                                </div>
                                <div>
                                  <p className="font-medium">
                                    Helicopter Transfer
                                  </p>
                                  <p className="text-gray-600">Requested</p>
                                </div>
                              </div>
                            )}
                            {transfer.assignedDriver && (
                              <div className="flex items-center text-sm mt-4">
                                <div className="w-5 h-5 rounded bg-green-100 flex items-center justify-center mr-2">
                                  <FaUserTie className="text-green-600 w-3 h-3" />
                                </div>
                                <div>
                                  <p className="font-medium">Assigned Driver</p>
                                  <p className="text-gray-600">
                                    {transfer.assignedDriver.name}{" "}
                                    {transfer.assignedDriver.surname}
                                  </p>
                                  <p className="text-gray-500 text-xs">
                                    {transfer.assignedDriver.phone}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {(transfer.additionalInfo || transfer.driverNotes) && (
                        <div className="mt-6">
                          <button
                            onClick={() =>
                              setExpandedTransfer(
                                expandedTransfer === transfer._id
                                  ? null
                                  : transfer._id
                              )
                            }
                            className="flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
                          >
                            {expandedTransfer === transfer._id ? (
                              <>
                                <FaChevronUp className="mr-1" />
                                Hide Notes
                              </>
                            ) : (
                              <>
                                <FaChevronDown className="mr-1" />
                                Show Notes
                              </>
                            )}
                          </button>

                          <AnimatePresence>
                            {expandedTransfer === transfer._id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="mt-4 grid grid-cols-1 gap-4">
                                  {transfer.additionalInfo && (
                                    <div className="bg-gray-50 rounded-lg p-4">
                                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                                        Customer Notes
                                      </h4>
                                      <p className="text-sm text-gray-600">
                                        {transfer.additionalInfo}
                                      </p>
                                    </div>
                                  )}

                                  {transfer.driverNotes && (
                                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                                      <h4 className="text-sm font-medium text-blue-700 mb-2">
                                        Driver Notes
                                      </h4>
                                      <p className="text-sm text-blue-600">
                                        {transfer.driverNotes}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      )}
                    </motion.div>
                  ))}
              </div>
            </>
          )}

          {activeTab === "transferPricing" && <TransferePage />}

          {activeTab === "tours" && (
            <>
              <div className="bg-gray-100 p-6 rounded-lg shadow-md border border-gray-200 mb-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900">
                    Tour Management
                  </h3>
 <motion.button
      onClick={handleCreateTour}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.98 }}
      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg hover:from-blue-700 hover:to-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 shadow-lg flex items-center"
    >
      <FaPlus className="mr-2" />
      <span>Create New Tour</span>
    </motion.button>
                </div>

                {/* Tours Statistics */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-br from-indigo-500 to-indigo-700 p-6 rounded-xl shadow-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-full bg-indigo-400 bg-opacity-40">
                        <FaSuitcase className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <p className="text-white text-sm opacity-80">
                          Total Tours
                        </p>
                        <p className="text-2xl font-bold text-white">
                          {toursData.length}
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-gradient-to-br from-green-500 to-green-700 p-6 rounded-xl shadow-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-full bg-green-400 bg-opacity-40">
                        <FaCheck className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <p className="text-white text-sm opacity-80">Active</p>
                        <p className="text-2xl font-bold text-white">
                          {toursData.filter((tour) => tour.isActive).length}
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-gradient-to-br from-purple-500 to-purple-700 p-6 rounded-xl shadow-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-full bg-purple-400 bg-opacity-40">
                        <FaUsers className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <p className="text-white text-sm opacity-80">
                          Booked Tours
                        </p>
                        <p className="text-2xl font-bold text-white">
                          {tourBookingsData.length}
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-gradient-to-br from-amber-500 to-amber-700 p-6 rounded-xl shadow-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-full bg-amber-400 bg-opacity-40">
                        <FaClock className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-right">
                        <p className="text-white text-sm opacity-80">
                          Pending Tours
                        </p>
                        <p className="text-2xl font-bold text-white">
                          {
                            tourBookingsData.filter(
                              (booking) => booking.status === "pending"
                            ).length
                          }
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Tours List */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Available Tours
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {toursData.map((tour) => (
                      <motion.div
                        key={tour._id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        whileHover={{ y: -5 }}
                        transition={{ duration: 0.2 }}
                        className={`bg-white rounded-xl shadow-md border overflow-hidden transition-all duration-300 ${
                          !tour.isActive ? "opacity-60" : ""
                        }`}
                      >
                        <div className="relative h-48 bg-blue-50">
                          {tour.imageUrl ? (
                            <img
                              src={
                                tour.imageUrl.startsWith("http")
                                  ? tour.imageUrl
                                  : `${"http://localhost:5000"}${
                                      tour.imageUrl.startsWith("/") ? "" : "/"
                                    }${tour.imageUrl}`
                              }
                              alt={tour.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/images/placeholder-tour.jpg";
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-100">
                              <FaSuitcase className="w-12 h-12 text-gray-300" />
                            </div>
                          )}
                          <div className="absolute top-4 right-4">
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                tour.isActive
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {tour.isActive ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </div>

                        <div className="p-5">
                          <div className="flex justify-between items-start">
                            <h5 className="text-xl font-bold text-gray-900 mb-2">
                              {tour.name}
                            </h5>
                            <div className="flex space-x-1">
                              <button
                                onClick={() => handleEditTour(tour._id)}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                              >
                                <FaEdit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  Swal.fire({
                                    title: "Delete Tour?",
                                    text: "This will permanently delete this tour. Are you sure?",
                                    icon: "warning",
                                    showCancelButton: true,
                                    confirmButtonColor: "#d33",
                                    cancelButtonColor: "#3085d6",
                                    confirmButtonText: "Yes, delete it!",
                                  }).then((result) => {
                                    if (result.isConfirmed) {
                                      tours
                                        .delete(tour._id)
                                        .then(() => {
                                          Swal.fire(
                                            "Deleted!",
                                            "The tour has been deleted.",
                                            "success"
                                          );
                                          fetchData(); // Refresh tour data
                                        })
                                        .catch((error) => {
                                          console.error(
                                            "Error deleting tour:",
                                            error
                                          );
                                          Swal.fire({
                                            icon: "error",
                                            title: "Deletion Failed",
                                            text: "Failed to delete tour. Please try again.",
                                            confirmButtonColor: "#1e3a8a",
                                          });
                                        });
                                    }
                                  });
                                }}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                              >
                                <FaTrash className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          <div className="mt-2">
                            <span className="text-sm text-gray-500">
                              {tour.duration} hours • Max {tour.maxPersons}{" "}
                              persons
                            </span>
                            <p className="mt-2 text-sm text-gray-600 line-clamp-3">
                              <HTMLContent html={tour.description} />
                            </p>
                          </div>

                          <div className="mt-3 flex flex-wrap gap-2">

                                <span
                                  className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  <FaMapMarkerAlt className="mr-1 w-3 h-3" />
                                  <HTMLContent html={tour.locations} />
                                </span>
                              
                            {tour.locations.length > 3 && (
                              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                                +{tour.locations.length - 3} more
                              </span>
                            )}
                          </div>

                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="text-xs text-gray-500">
                                  Starting from
                                </p>
                                <div>
                                  {(() => {
                                    // Determine current season based on today's date
                                    const today = new Date();
                                    const month = today.getMonth() + 1; // JavaScript months are 0-based
                                    const day = today.getDate();

                                    // Default to low season if not matched
                                    let currentSeason = "low";

                                    // Check if current date falls within medium season
                                    if (
                                      (month === 6 && day >= 26) ||
                                      (month === 7 && day <= 10) ||
                                      (month === 8 && day >= 26) ||
                                      (month === 9 && day <= 15)
                                    ) {
                                      currentSeason = "medium";
                                    }
                                    // Check if current date falls within high season
                                    else if (
                                      (month === 7 && day >= 11) ||
                                      (month === 8 && day <= 25)
                                    ) {
                                      currentSeason = "high";
                                    }

                                    // Get price based on vehicle type and season
                                    const price =
                                      tour.vehicleType === "car"
                                        ? tour.pricing.car[
                                            `${currentSeason}Season`
                                          ]
                                        : tour.vehicleType === "minivan"
                                        ? tour.pricing.minivan[
                                            `${currentSeason}Season`
                                          ]
                                        : tour.vehicleType === "van"
                                        ? tour.pricing.van[
                                            `${currentSeason}Season`
                                          ]
                                        : tour.pricing.minibus[
                                            `${currentSeason}Season`
                                          ];

                                    return (
                                      <>
                                        <p className="text-lg font-bold text-blue-600">
                                          €{price}
                                        </p>
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  tours
                                    .toggleActive(tour._id)
                                    .then(() => {
                                      Swal.fire({
                                        icon: "success",
                                        title: "Tour Status Updated!",
                                        text: `Tour has been ${
                                          tour.isActive
                                            ? "deactivated"
                                            : "activated"
                                        }.`,
                                        confirmButtonColor: "#1e3a8a",
                                      });
                                      fetchData(); // Refresh tour data
                                    })
                                    .catch((error) => {
                                      console.error(
                                        "Error toggling tour status:",
                                        error
                                      );
                                      Swal.fire({
                                        icon: "error",
                                        title: "Update Failed",
                                        text: "Failed to update tour status. Please try again.",
                                        confirmButtonColor: "#1e3a8a",
                                      });
                                    });
                                }}
                                className={`px-4 py-2 text-xs font-medium rounded-lg ${
                                  tour.isActive
                                    ? "bg-red-100 text-red-800 hover:bg-red-200"
                                    : "bg-green-100 text-green-800 hover:bg-green-200"
                                }`}
                              >
                                {tour.isActive ? "Deactivate" : "Activate"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Tour Bookings */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Tour Bookings
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white rounded-lg overflow-hidden">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tour
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date & Time
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Persons
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Price
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Vehicle
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Pickup Location
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Payment
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Travel Guide
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Customer
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {tourBookingsData.map((booking) => {
                          return (
                            <tr key={booking._id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {booking.tour?.name}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {moment(booking.tourDate).format("MMM D, YYYY")}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {booking.tourTime?.hours}:{booking.tourTime?.minutes}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {booking.numberOfPersons}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  €{booking.totalPrice}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900 capitalize">
                                  {booking.vehicleType}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {booking.pickupLocation}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900 capitalize">
                                  {booking.paymentMethod?.replace("-", " ")}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {booking.travelGuide.available ? 'Yes, for ' + booking.travelGuide.time : 'No'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button
                                  className="px-3 py-1 rounded bg-blue-100 text-blue-800 text-xs font-semibold hover:bg-blue-200 transition"
                                  onClick={() => {
                                    Swal.fire({
                                      title: "Customer Information",
                                      html: `
                                        <div class="text-left space-y-2">
                                          <div>
                                            <span class="font-semibold">Name:</span>
                                            <span>${booking.name || ""}</span>
                                          </div>
                                          <div>
                                            <span class="font-semibold">Email:</span>
                                            <span>${booking.email || ""}</span>
                                          </div>
                                          <div>
                                            <span class="font-semibold">Phone:</span>
                                            <span>${booking.phone || ""}</span>
                                          </div>
                                          <div>
                                            <span class="font-semibold">Notes:</span>
                                            <span>${booking.notes || "-"}</span>
                                          </div>
                                          <div>
                                            <span class="font-semibold">Created:</span>
                                            <span>${moment(booking.createdAt).format("MMM D, YYYY HH:mm")}</span>
                                          </div>
                                        </div>
                                      `,
                                      confirmButtonColor: "#1e3a8a",
                                    });
                                  }}
                                >
                                  View
                                </button>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                    ${
                                      booking.status === "pending"
                                        ? "bg-yellow-100 text-yellow-800"
                                        : booking.status === "confirmed"
                                        ? "bg-blue-100 text-blue-800"
                                        : booking.status === "completed"
                                        ? "bg-green-100 text-green-800"
                                        : "bg-red-100 text-red-800"
                                    }`}
                                >
                                  {booking.status.charAt(0).toUpperCase() +
                                    booking.status.slice(1)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex space-x-2">
                                  <select
                                    value={booking.status}
                                    onChange={(e) => {
                                      tourBookings
                                        .updateStatus(booking._id, e.target.value)
                                        .then(() => {
                                          Swal.fire({
                                            icon: "success",
                                            title: "Status Updated!",
                                            text: `Booking status changed.`,
                                            confirmButtonColor: "#1e3a8a",
                                            timer: 1500,
                                          });
                                          fetchData();
                                        })
                                        .catch((error) => {
                                          console.error(
                                            "Error updating booking status:",
                                            error
                                          );
                                          Swal.fire({
                                            icon: "error",
                                            title: "Update Failed",
                                            text: "Failed to update booking status.",
                                            confirmButtonColor: "#1e3a8a",
                                          });
                                        });
                                    }}
                                    className="text-sm border-gray-300 rounded-lg p-2"
                                  >
                                    <option value="pending">Pending</option>
                                    <option value="confirmed">Confirmed</option>
                                    <option value="completed">Completed</option>
                                    <option value="cancelled">Cancelled</option>
                                  </select>
                                  <button
                                    onClick={() => {
                                      Swal.fire({
                                        title: "Delete Booking?",
                                        text: "Are you sure you want to delete this tour booking?",
                                        icon: "warning",
                                        showCancelButton: true,
                                        confirmButtonColor: "#d33",
                                        cancelButtonColor: "#3085d6",
                                        confirmButtonText: "Yes, delete it!",
                                      }).then((result) => {
                                        if (result.isConfirmed) {
                                          tourBookings
                                            .delete(booking._id)
                                            .then(() => {
                                              Swal.fire(
                                                "Deleted!",
                                                "The booking has been deleted.",
                                                "success"
                                              );
                                              fetchData();
                                            })
                                            .catch((error) => {
                                              console.error(
                                                "Error deleting booking:",
                                                error
                                              );
                                              Swal.fire({
                                                icon: "error",
                                                title: "Deletion Failed",
                                                text: "Failed to delete booking.",
                                                confirmButtonColor: "#1e3a8a",
                                              });
                                            });
                                        }
                                      });
                                    }}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <FaTrash className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {tourBookingsData.length === 0 && (
                          <tr>
                            <td
                              colSpan="10"
                              className="px-6 py-4 text-center text-sm text-gray-500"
                            >
                              No tour bookings found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === "drivers" && (
            <div className="space-y-6">
              {/* Drivers Statistics */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-br from-blue-500 to-blue-700 p-6 rounded-xl shadow-lg"
                >
                  <div className="flex items-center justify-between">
                    <div className="p-3 rounded-full bg-blue-400 bg-opacity-40">
                      <FaUserTie className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-white text-sm opacity-80">
                        Total Drivers
                      </p>
                      <p className="text-2xl font-bold text-white">
                        {driversData.length}
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-gradient-to-br from-green-500 to-green-700 p-6 rounded-xl shadow-lg"
                >
                  <div className="flex items-center justify-between">
                    <div className="p-3 rounded-full bg-green-400 bg-opacity-40">
                      <FaCheck className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-white text-sm opacity-80">Active</p>
                      <p className="text-2xl font-bold text-white">
                        {driversData.filter((d) => d.isActive).length}
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-gradient-to-br from-yellow-500 to-yellow-700 p-6 rounded-xl shadow-lg"
                >
                  <div className="flex items-center justify-between">
                    <div className="p-3 rounded-full bg-yellow-400 bg-opacity-40">
                      <FaIdCard className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-white text-sm opacity-80">
                        License Expiring Soon
                      </p>
                      <p className="text-2xl font-bold text-white">
                        {
                          driversData.filter((d) => {
                            const daysUntilExpiry = Math.ceil(
                              (new Date(d.licenseExpiry) - new Date()) /
                                (1000 * 60 * 60 * 24)
                            );
                            return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
                          }).length
                        }
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-gradient-to-br from-purple-500 to-purple-700 p-6 rounded-xl shadow-lg"
                >
                  <div className="flex items-center justify-between">
                    <div className="p-3 rounded-full bg-purple-400 bg-opacity-40">
                      <FaTaxi className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-white text-sm opacity-80">On Duty</p>
                      <p className="text-2xl font-bold text-white">
                        {
                          transfersData.filter(
                            (t) =>
                              t.assignedDriver &&
                              (t.status === "confirmed" ||
                                t.status === "pending") &&
                              new Date(t.pickUpDate) >= new Date()
                          ).length
                        }
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Search and Filter Section */}
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search drivers..."
                      value={driverSearch}
                      onChange={(e) => setDriverSearch(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 pl-10"
                    />
                    <FaSearch className="absolute left-3 top-3 text-gray-400" />
                  </div>

                  <div>
                    <select
                      value={driverVehicleTypeFilter}
                      onChange={(e) =>
                        setDriverVehicleTypeFilter(e.target.value)
                      }
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    >
                      <option value="all">All Vehicle Types</option>
                      <option value="car">Car</option>
                      <option value="minivan">Mini Van</option>
                      <option value="van">Van</option>
                      <option value="minibus">Mini Bus</option>
                    </select>
                  </div>

                  <div>
                    <motion.button
                      onClick={handleDriverCreate}
                      disabled={creatingDriver}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-lg hover:from-purple-700 hover:to-purple-900 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg"
                    >
                      {creatingDriver ? (
                        <div className="flex items-center justify-center">
                          <LoadingSpinner size="sm" />
                          <span className="ml-2">Creating...</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <FaPlus className="mr-2" />
                          <span>Add New Driver</span>
                        </div>
                      )}
                    </motion.button>
                  </div>
                </div>
              </div>

              {/* Drivers List */}
              <div className="grid grid-cols-1 gap-6">
                {driversData
                  .filter((driver) => {
                    const matchesSearch =
                      (driver.name + " " + driver.surname)
                        .toLowerCase()
                        .includes(driverSearch.toLowerCase()) ||
                      driver.email
                        .toLowerCase()
                        .includes(driverSearch.toLowerCase()) ||
                      driver.phone
                        .toLowerCase()
                        .includes(driverSearch.toLowerCase()) ||
                      driver.licenseNumber
                        .toLowerCase()
                        .includes(driverSearch.toLowerCase());

                    const matchesVehicleType =
                      driverVehicleTypeFilter === "all" ||
                      driver.vehicleTypes.includes(driverVehicleTypeFilter);

                    return matchesSearch && matchesVehicleType;
                  })
                  .sort((a, b) => {
                    // Sort by active status first, then by name
                    if (a.isActive && !b.isActive) return -1;
                    if (!a.isActive && b.isActive) return 1;
                    return a.name.localeCompare(b.name);
                  })
                  .map((driver) => {
                    const daysUntilLicenseExpiry = Math.ceil(
                      (new Date(driver.licenseExpiry) - new Date()) /
                        (1000 * 60 * 60 * 24)
                    );

                    const isLicenseExpired = daysUntilLicenseExpiry <= 0;
                    const isLicenseExpiringSoon =
                      daysUntilLicenseExpiry > 0 &&
                      daysUntilLicenseExpiry <= 30;

                    // Find current assignments for this driver
                    const currentAssignments = transfersData.filter(
                      (transfer) =>
                        transfer.assignedDriver &&
                        transfer.assignedDriver._id === driver._id &&
                        (transfer.status === "confirmed" ||
                          transfer.status === "pending") &&
                        new Date(transfer.pickUpDate) >= new Date()
                    );

                    return (
                      <motion.div
                        key={driver._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className={`bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 ${
                          !driver.isActive ? "opacity-75" : ""
                        }`}
                      >
                        <div className="p-6">
                          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                            {/* Driver Info */}
                            <div className="flex items-start space-x-4">
                              <div
                                className={`${
                                  driver.isActive
                                    ? "bg-green-100"
                                    : "bg-gray-100"
                                } rounded-full p-3`}
                              >
                                <FaUserTie
                                  className={`w-6 h-6 ${
                                    driver.isActive
                                      ? "text-green-600"
                                      : "text-gray-400"
                                  }`}
                                />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="text-lg font-semibold text-gray-900">
                                    {driver.name} {driver.surname}
                                  </h3>
                                  {isLicenseExpired && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      License Expired
                                    </span>
                                  )}
                                  {isLicenseExpiringSoon && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                      License Expiring Soon
                                    </span>
                                  )}
                                  {!driver.isActive && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                      Inactive
                                    </span>
                                  )}
                                </div>
                                <div className="flex flex-col sm:flex-row sm:space-x-4">
                                  <p className="text-sm text-gray-500 flex items-center">
                                    <FaEnvelope className="w-4 h-4 mr-1" />{" "}
                                    {driver.email}
                                  </p>
                                  <p className="text-sm text-gray-500 flex items-center">
                                    <FaPhoneAlt className="w-4 h-4 mr-1" />{" "}
                                    {driver.phone}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-wrap items-center gap-3">
                              <button
                                onClick={() => handleDriverEdit(driver._id)}
                                disabled={editingDriver === driver._id}
                                className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200"
                              >
                                {editingDriver === driver._id ? (
                                  <span className="flex items-center">
                                    <LoadingSpinner size="sm" />
                                    <span className="ml-2">Editing...</span>
                                  </span>
                                ) : (
                                  <span className="flex items-center">
                                    <FaEdit className="mr-2" />
                                    Edit
                                  </span>
                                )}
                              </button>

                              <button
                                onClick={() =>
                                  handleDriverPasswordChange(driver._id)
                                }
                                disabled={changingDriverPassword === driver._id}
                                className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-purple-100 text-purple-800 hover:bg-purple-200"
                              >
                                {changingDriverPassword === driver._id ? (
                                  <span className="flex items-center">
                                    <LoadingSpinner size="sm" />
                                    <span className="ml-2">Updating...</span>
                                  </span>
                                ) : (
                                  <span className="flex items-center">
                                    <FaCog className="mr-2" />
                                    Change Password
                                  </span>
                                )}
                              </button>

                              <button
                                onClick={() => handleDriverDelete(driver._id)}
                                disabled={deletingDriverId === driver._id}
                                className="inline-flex items-center px-3 py-2 border border-red-200 text-red-600 rounded-md hover:bg-red-50"
                              >
                                <FaTrash className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {/* Driver Details */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* License Info */}
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium text-gray-700">
                                License Information
                              </h4>
                              <div className="bg-gray-50 rounded-lg p-4">
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500">
                                      License Number:
                                    </span>
                                    <span className="font-medium">
                                      {driver.licenseNumber}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500">
                                      Expiry Date:
                                    </span>
                                    <span
                                      className={`font-medium ${
                                        isLicenseExpired
                                          ? "text-red-600"
                                          : isLicenseExpiringSoon
                                          ? "text-yellow-600"
                                          : ""
                                      }`}
                                    >
                                      {new Date(
                                        driver.licenseExpiry
                                      ).toLocaleDateString()}
                                    </span>
                                  </div>
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500">
                                      Status:
                                    </span>
                                    <span
                                      className={`font-medium ${
                                        isLicenseExpired
                                          ? "text-red-600"
                                          : "text-green-600"
                                      }`}
                                    >
                                      {isLicenseExpired
                                        ? "Expired"
                                        : daysUntilLicenseExpiry <= 30
                                        ? `Expires in ${daysUntilLicenseExpiry} days`
                                        : "Valid"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Vehicle Types */}
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium text-gray-700">
                                Vehicle Types
                              </h4>
                              <div className="bg-gray-50 rounded-lg p-4">
                                <div className="flex flex-wrap gap-2">
                                  {driver.vehicleTypes.map((type) => (
                                    <span
                                      key={type}
                                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                    >
                                      {type === "car" && (
                                        <FaCar className="mr-1" />
                                      )}
                                      {type === "minivan" && (
                                        <FaTaxi className="mr-1" />
                                      )}
                                      {type === "van" && (
                                        <FaTruckMoving className="mr-1" />
                                      )}
                                      {type === "minibus" && (
                                        <FaTruckMoving className="mr-1" />
                                      )}
                                      {type.charAt(0).toUpperCase() +
                                        type.slice(1)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>

                            {/* Current Assignments */}
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium text-gray-700">
                                Current Assignments
                              </h4>
                              <div className="bg-gray-50 rounded-lg p-4">
                                {currentAssignments.length === 0 ? (
                                  <p className="text-sm text-gray-500">
                                    No current assignments
                                  </p>
                                ) : (
                                  <div className="space-y-3">
                                    {currentAssignments
                                      .slice(0, 2)
                                      .map((transfer) => (
                                        <div
                                          key={transfer._id}
                                          className="text-sm border-l-2 border-blue-500 pl-3"
                                        >
                                          <p className="font-medium">
                                            {transfer.pickupLocation} →{" "}
                                            {transfer.dropoffLocation}
                                          </p>
                                          <p className="text-gray-500">
                                            {moment(transfer.pickUpDate).format(
                                              "DD/MM/YYYY"
                                            )}{" "}
                                            at {transfer.pickupHours}:
                                            {transfer.pickupMinutes}
                                          </p>
                                        </div>
                                      ))}
                                    {currentAssignments.length > 2 && (
                                      <p className="text-xs text-blue-600 font-medium">
                                        {currentAssignments.length - 2} more
                                        assignments
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Notes */}
                          {driver.notes && (
                            <div className="mt-6 bg-yellow-50 rounded-lg p-4 border border-yellow-100">
                              <h4 className="text-sm font-medium text-yellow-800 mb-2">
                                Notes
                              </h4>
                              <p className="text-sm text-yellow-700">
                                {driver.notes}
                              </p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
              </div>
            </div>
          )}

          {activeTab === "finances" && <FinanceManagement />}

          {activeTab === "coupons" && (
            <div className="space-y-8 animate-fadeIn">
              {/* Coupon Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl shadow-sm border border-blue-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-blue-500/20 text-blue-600">
                      <FaTag className="w-6 h-6" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-blue-700">
                        Active Coupons
                      </h3>
                      <p className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 text-transparent bg-clip-text">
                        {
                          couponsData.filter(
                            (c) => new Date(c.expiryDate) > new Date()
                          ).length
                        }
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl shadow-sm border border-green-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-green-500/20 text-green-600">
                      <FaPercent className="w-6 h-6" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-green-700">
                        Total Uses
                      </h3>
                      <p className="text-2xl font-bold bg-gradient-to-r from-green-600 to-green-800 text-transparent bg-clip-text">
                        {couponsData.reduce(
                          (total, c) => total + c.usageCount,
                          0
                        )}
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-xl shadow-sm border border-amber-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-amber-500/20 text-amber-600">
                      <FaClock className="w-6 h-6" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-amber-700">
                        Expiring Soon
                      </h3>
                      <p className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-amber-800 text-transparent bg-clip-text">
                        {
                          couponsData.filter((c) => {
                            const daysUntilExpiry = Math.ceil(
                              (new Date(c.expiryDate) - new Date()) /
                                (1000 * 60 * 60 * 24)
                            );
                            return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
                          }).length
                        }
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-xl shadow-sm border border-red-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center">
                    <div className="p-3 rounded-full bg-red-500/20 text-red-600">
                      <FaBan className="w-6 h-6" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-red-700">
                        Expired
                      </h3>
                      <p className="text-2xl font-bold bg-gradient-to-r from-red-600 to-red-800 text-transparent bg-clip-text">
                        {
                          couponsData.filter(
                            (c) => new Date(c.expiryDate) <= new Date()
                          ).length
                        }
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Duration-based Discounts Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all hover:shadow-md"
              >
                <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Duration-Based Discounts
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Configure automatic discounts based on rental duration
                  </p>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                      {
                        term: "shortTerm",
                        label: "Short Term (1-3 days)",
                        color: "blue",
                        icon: <FaCalendarDay className="mr-2" />,
                      },
                      {
                        term: "mediumTerm",
                        label: "Medium Term (4-10 days)",
                        color: "green",
                        icon: <FaCalendarWeek className="mr-2" />,
                      },
                      {
                        term: "longTerm",
                        label: "Long Term (10+ days)",
                        color: "purple",
                        icon: <FaCalendarAlt className="mr-2" />,
                      },
                    ].map(({ term, label, color, icon }) => (
                      <div
                        key={term}
                        className={`relative bg-${color}-50 p-4 rounded-lg border border-${color}-200 transition-all hover:shadow-sm`}
                      >
                        <label
                          className={`flex items-center text-sm font-medium text-${color}-700 mb-3`}
                        >
                          {icon} {label}
                        </label>
                        <div className="flex items-center">
                          <div className="relative flex-1 mr-4">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              step="5"
                              value={durationDiscounts[term].discount}
                              onChange={(e) =>
                                handleDurationDiscountUpdate(
                                  term,
                                  e.target.value
                                )
                              }
                              className={`w-full h-2 bg-${color}-200 rounded-lg appearance-none cursor-pointer`}
                            />
                          </div>
                          <div className="w-16 relative">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={durationDiscounts[term].discount}
                              onChange={(e) =>
                                handleDurationDiscountUpdate(
                                  term,
                                  e.target.value
                                )
                              }
                              className={`block w-full rounded-lg border border-${color}-300 text-center focus:border-${color}-500 focus:ring-${color}-500 p-2`}
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                              <span className="text-gray-500">%</span>
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 text-center">
                          <span
                            className={`text-${color}-600 text-sm font-medium`}
                          >
                            {durationDiscounts[term].discount}% discount
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Coupon Creation/Edit Form */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all hover:shadow-md"
              >
                <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <FaTicketAlt className="mr-2 text-indigo-600" />
                    {editingCoupon ? "Edit Coupon" : "Create New Coupon"}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {editingCoupon
                      ? "Modify existing coupon details"
                      : "Create a new discount coupon for your customers"}
                  </p>
                </div>
                <div className="p-6">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target);
                      const couponData = {
                        code: formData.get("code").toUpperCase(),
                        discountType: formData.get("discountType"),
                        discountValue: Number(formData.get("discountValue")),
                        minOrderAmount: Number(formData.get("minOrderAmount")),
                        expiryDate: formData.get("expiryDate"),
                        maxUses: Number(formData.get("maxUses")),
                      };

                      if (editingCoupon) {
                        handleCouponEdit(couponData);
                      } else {
                        handleCouponCreate(couponData);
                      }
                    }}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                  >
                    <div className="space-y-2">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <FaTag className="mr-2 text-blue-500" /> Coupon Code
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="code"
                          required
                          defaultValue={editingCoupon?.code || ""}
                          placeholder="e.g., SUMMER2025"
                          className="block w-full rounded-lg border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 transition-all uppercase"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <FaPercentage className="mr-2 text-green-500" />{" "}
                        Discount Type
                      </label>
                      <div className="relative">
                        <select
                          name="discountType"
                          required
                          defaultValue={
                            editingCoupon?.discountType || "percentage"
                          }
                          className="block w-full rounded-lg border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 transition-all appearance-none"
                        >
                          <option value="percentage">Percentage (%)</option>
                          <option value="fixed">Fixed Amount (€)</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                          <FaChevronDown className="h-4 w-4" />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <FaEuroSign className="mr-2 text-amber-500" /> Discount
                        Value
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          name="discountValue"
                          required
                          defaultValue={editingCoupon?.discountValue || ""}
                          placeholder="Enter amount or percentage"
                          className="block w-full rounded-lg border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 transition-all"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <FaShoppingCart className="mr-2 text-purple-500" />{" "}
                        Minimum Order Amount
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                          <span className="text-gray-500">€</span>
                        </div>
                        <input
                          type="number"
                          name="minOrderAmount"
                          required
                          defaultValue={editingCoupon?.minOrderAmount || ""}
                          placeholder="Minimum order value"
                          className="block w-full rounded-lg border-0 bg-gray-50 pl-8 pr-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 transition-all"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <FaCalendarAlt className="mr-2 text-red-500" /> Expiry
                        Date
                      </label>
                      <div className="relative">
                        <input
                          type="date"
                          name="expiryDate"
                          required
                          defaultValue={
                            editingCoupon?.expiryDate
                              ? new Date(editingCoupon.expiryDate)
                                  .toISOString()
                                  .split("T")[0]
                              : ""
                          }
                          className="block w-full rounded-lg border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 transition-all"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        <FaUsers className="mr-2 text-blue-500" /> Maximum Uses
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          name="maxUses"
                          required
                          defaultValue={editingCoupon?.maxUses || ""}
                          placeholder="Maximum number of uses"
                          className="block w-full rounded-lg border-0 bg-gray-50 px-4 py-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 transition-all"
                        />
                      </div>
                    </div>

                    <div className="md:col-span-2 lg:col-span-3 flex gap-4">
                      {editingCoupon && (
                        <button
                          type="button"
                          onClick={() => setEditingCoupon(null)}
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        type="submit"
                        disabled={creatingCoupon}
                        className="flex-1 flex justify-center items-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        {creatingCoupon ? (
                          <>
                            <LoadingSpinner size="sm" />
                            <span className="ml-2">
                              {editingCoupon ? "Updating..." : "Creating..."}
                            </span>
                          </>
                        ) : (
                          <span className="flex items-center">
                            {editingCoupon ? (
                              <>
                                <FaEdit className="mr-2" /> Update Coupon
                              </>
                            ) : (
                              <>
                                <FaPlus className="mr-2" /> Create Coupon
                              </>
                            )}
                          </span>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>

              {/* Active Coupons Grid */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <h3 className="text-xl font-bold text-gray-800 flex items-center">
                  <FaTicketAlt className="mr-2 text-indigo-600" />
                  Active Coupons
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <AnimatePresence mode="wait">
                    {couponsData.map((coupon, index) => {
                      const isExpired =
                        new Date(coupon.expiryDate) <= new Date();
                      const usagePercentage =
                        (coupon.usageCount / coupon.maxUses) * 100;
                      const expiryDate = new Date(coupon.expiryDate);
                      const daysRemaining = Math.ceil(
                        (expiryDate - new Date()) / (1000 * 60 * 60 * 24)
                      );

                      // Determine status color
                      let statusColor = "green";
                      if (isExpired) {
                        statusColor = "red";
                      } else if (daysRemaining <= 3) {
                        statusColor = "red";
                      } else if (daysRemaining <= 7) {
                        statusColor = "amber";
                      }

                      return (
                        <motion.div
                          key={coupon._id}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95, y: 20 }}
                          transition={{ delay: index * 0.05 }}
                          className={`bg-white rounded-xl shadow-sm border-2 ${
                            isExpired ? "border-red-300" : "border-gray-100"
                          } overflow-hidden hover:shadow-md transition-all duration-300 relative`}
                        >
                          <div
                            className={`h-2 w-full ${
                              isExpired
                                ? "bg-red-500"
                                : usagePercentage > 75
                                ? "bg-amber-500"
                                : "bg-green-500"
                            }`}
                          ></div>

                          <div className="p-6">
                            <div className="flex justify-between items-start mb-4">
                              <div className="space-y-3">
                                <div className="flex items-center space-x-2">
                                  <span className="px-3 py-1 rounded-full text-sm font-bold bg-indigo-100 text-indigo-800">
                                    {coupon.code}
                                  </span>
                                  {isExpired && (
                                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 animate-pulse">
                                      EXPIRED
                                    </span>
                                  )}
                                </div>
                                <div className="space-y-1">
                                  <p className="text-2xl font-bold text-gray-900 flex items-center">
                                    {coupon.discountType === "percentage" ? (
                                      <>
                                        <span className="bg-gradient-to-r from-indigo-600 to-indigo-800 text-transparent bg-clip-text">
                                          {coupon.discountValue}%
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <span className="bg-gradient-to-r from-blue-600 to-indigo-800 text-transparent bg-clip-text">
                                          €{coupon.discountValue}
                                        </span>
                                      </>
                                    )}
                                    <span className="text-sm font-normal text-gray-500 ml-1">
                                      discount
                                    </span>
                                  </p>
                                  <p className="text-sm text-gray-600 flex items-center">
                                    <FaShoppingCart className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                                    Minimum order:{" "}
                                    <span className="font-medium text-gray-800 ml-1">
                                      €{coupon.minOrderAmount}
                                    </span>
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setEditingCoupon(coupon)}
                                  className="p-2 rounded-full hover:bg-blue-50 text-gray-400 hover:text-blue-500 focus:outline-none transition-all"
                                >
                                  <FaEdit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleCouponDelete(coupon._id)}
                                  disabled={deletingCouponId === coupon._id}
                                  className="p-2 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                  {deletingCouponId === coupon._id ? (
                                    <LoadingSpinner size="sm" />
                                  ) : (
                                    <FaTrash className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            </div>

                            <div className="px-6 pb-5 pt-3">
                              {/* Usage indicator with animation */}
                              <div className="mb-2 flex justify-between items-center text-xs text-gray-500">
                                <span>Usage</span>
                                <span className="font-medium">
                                  {coupon.usageCount} of {coupon.maxUses}
                                </span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                                <div
                                  className={`h-2 rounded-full ${
                                    isExpired
                                      ? "bg-gray-300"
                                      : usagePercentage > 75
                                      ? "bg-red-500"
                                      : usagePercentage > 50
                                      ? "bg-amber-500"
                                      : "bg-green-500"
                                  } transition-all duration-500 ease-out`}
                                  style={{
                                    width: `${usagePercentage}%`,
                                  }}
                                />
                              </div>

                              {/* Expiry countdown */}
                              <div className="mt-4 flex justify-between items-center text-xs">
                                <div
                                  className={`flex items-center ${
                                    isExpired
                                      ? "text-red-500"
                                      : daysRemaining <= 3
                                      ? "text-red-500"
                                      : daysRemaining <= 7
                                      ? "text-amber-500"
                                      : "text-green-600"
                                  }`}
                                >
                                  <FaCalendarAlt className="w-3.5 h-3.5 mr-1.5" />
                                  {isExpired ? (
                                    <span className="font-medium">
                                      Expired on{" "}
                                      {expiryDate.toLocaleDateString()}
                                    </span>
                                  ) : (
                                    <span className="font-medium">
                                      {daysRemaining === 0
                                        ? "Expires today"
                                        : daysRemaining === 1
                                        ? "Expires tomorrow"
                                        : `${daysRemaining} days remaining`}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              </motion.div>
            </div>
          )}

          {activeTab === "partners" && <PartnerManagement />}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AdminDashboard;
